import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import StatusBadge from "../components/StatusBadge";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Plus, Send, Bell, Check, X, MessageCircle, Camera, Home,
  ChevronRight, Circle, CheckCircle2, Loader2, DollarSign, UserPlus, Pencil,
  AlertOctagon, PauseCircle, ShieldAlert, Trophy, Upload, ArrowRight, MoreHorizontal, Lock,
  ClipboardCheck, RotateCcw, Paperclip, Image as ImageIcon, Eye, Trash2, Users,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import DocumentsTab from "../components/DocumentsTab";
import ProjectFeed from "../components/ProjectFeed";
import TaskImportDialog from "../components/TaskImportDialog";
import PaymentsTab, { PaymentRollupCard } from "../components/PaymentsTab";
import SendToContractorDialog from "../components/SendToContractorDialog";
import TaskContractorUpdates from "../components/TaskContractorUpdates";
import DefectsPanel, { countActionable } from "../components/DefectsPanel";
import ReviewImagesBeforeCustomerDialog from "../components/ReviewImagesBeforeCustomerDialog";
import VODialog from "../components/VODialog";
import VOAttachments from "../components/VOAttachments";
import VOTimeline from "../components/VOTimeline";
import ContractorCostTab from "./ContractorCostTab";
import { useConfirm as useConfirmHook } from "../components/ConfirmProvider";
import { useAuth } from "../context/AuthContext";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "tasks", label: "Project Hub" },
  { key: "contractor-cost", label: "Project Cost", firmOnly: true },
  { key: "payments", label: "Payments" },
  { key: "vos", label: "VOs" },
  { key: "defects", label: "Defects" },
  { key: "feed", label: "Updates" },
  { key: "documents", label: "Documents" },
  { key: "activity", label: "Activity" },
];

export default function ProjectDetail() {
  const { projectId } = useParams();
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const currentTab = sp.get("tab") || "overview";
  const { user } = useAuth();

  const [project, setProject] = useState(null);
  const [members, setMembers] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [vos, setVos] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [feed, setFeed] = useState([]);
  const [activity, setActivity] = useState([]);
  const [defects, setDefects] = useState([]);
  const [firmContractors, setFirmContractors] = useState([]);
  const [firmStaff, setFirmStaff] = useState([]);
  const [firmMilestoneLibrary, setFirmMilestoneLibrary] = useState([]);
  const [paymentStages, setPaymentStages] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    const [p, v, t, f, a, fc, fm, fs, ps, df] = await Promise.all([
      api.get(`/projects/${projectId}`),
      api.get(`/projects/${projectId}/vos`),
      api.get(`/projects/${projectId}/tasks`),
      api.get(`/projects/${projectId}/feed`),
      api.get(`/projects/${projectId}/activity`),
      api.get(`/workspace/contractors`).catch(() => ({ data: { contractors: [] } })),
      api.get(`/workspace/members`).catch(() => ({ data: { members: [] } })),
      api.get(`/firm/settings`).catch(() => ({ data: { firm: {} } })),
      api.get(`/projects/${projectId}/payment-stages`).catch(() => ({ data: { stages: [] } })),
      api.get(`/projects/${projectId}/defects`).catch(() => ({ data: { defects: [] } })),
    ]);
    setProject(p.data.project);
    setMembers(p.data.members || []);
    setMilestones(p.data.milestones || []);
    setVos(v.data.vos || []);
    setTasks(t.data.tasks || []);
    setFeed(f.data.feed || []);
    setActivity(a.data.activity || []);
    setFirmContractors(fc.data.contractors || []);
    setFirmStaff(fm.data.members || []);
    setFirmMilestoneLibrary(fs.data.firm?.milestone_templates || []);
    setPaymentStages(ps.data.stages || []);
    setDefects(df.data.defects || []);
    setLoading(false);
  }, [projectId]);

  useEffect(() => { reload(); }, [reload]);

  // Unread badge for the Updates tab — polls when NOT on the feed tab so ID
  // staff see fresh homeowner activity without a manual refresh.
  const [feedUnread, setFeedUnread] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const r = await api.get(`/projects/${projectId}/feed/unread-count`);
        if (!cancelled) setFeedUnread(r.data.unread || 0);
      } catch (err) { console.debug("feed unread poll failed", err); }
    };
    fetchUnread();
    const id = setInterval(() => {
      if (currentTab !== "feed") fetchUnread();
    }, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [projectId, currentTab]);
  const feedUnreadHook = useCallback((n) => setFeedUnread(n), []);

  const setTab = (t) => setSp({ tab: t }, { replace: true });

  // Milestone IDs that are gated by an unresolved (pending) payment stage.
  // These MUST be declared before any early returns so hook order stays stable.
  const gatedMilestoneIds = useMemo(() => {
    const set = new Set();
    paymentStages.forEach((s) => {
      if (s.status !== "pending") return;
      const ids = s.linked_milestone_ids || (s.linked_milestone_id ? [s.linked_milestone_id] : []);
      ids.forEach((id) => set.add(id));
    });
    return set;
  }, [paymentStages]);
  const pendingByMilestone = useMemo(() => {
    const m = new Map();
    paymentStages.forEach((s) => {
      if (s.status !== "pending") return;
      const ids = s.linked_milestone_ids || (s.linked_milestone_id ? [s.linked_milestone_id] : []);
      ids.forEach((id) => {
        m.set(id, (m.get(id) || 0) + (s.amount || 0));
      });
    });
    return m;
  }, [paymentStages]);

  if (loading) return <div className="max-w-7xl mx-auto py-10 text-[#7A7571]">Loading project…</div>;
  if (!project) return <div className="max-w-7xl mx-auto py-10 text-[#7A7571]">Not found</div>;

  // Progress % is now purely task-based (mirrors the Project Hub) — a task
  // counts as complete when it's `done` OR approved and forwarded to the customer.
  const doneTasks = tasks.filter((t) => t.status === "done" || (t.status === "waiting_on_customer" && !!t.approved_at)).length;
  const progressPct = tasks.length > 0 ? Math.round((doneTasks / tasks.length) * 100) : 0;
  const isReadOnly = project.status === "completed";
  const canEdit = ["admin","pm","designer"].includes(user?.role) && !isReadOnly;

  return (
    <div className="max-w-7xl mx-auto py-8">
      <button onClick={() => navigate("/projects")} className="inline-flex items-center gap-2 text-[#7A7571] hover:text-[#2C2A29] mb-6">
        <ArrowLeft size={16} /> All projects
      </button>

      {/* Read-only banner when project is completed */}
      {isReadOnly && (
        <div
          data-testid="project-readonly-banner"
          className="mb-6 rounded-2xl border border-[#86EFAC] bg-[#DCFCE7] px-4 py-3 flex items-center gap-3"
        >
          <div className="w-9 h-9 rounded-full bg-white/70 flex items-center justify-center text-[#166534] flex-shrink-0">
            <Lock size={16} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-[#166534]">This project is marked completed</div>
            <div className="text-xs text-[#166534]/80 mt-0.5">
              All milestones, tasks, VOs, and payment stages are frozen. Reopen it from the Projects page to make more changes.
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Project</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#2C2A29] mt-1">{project.name}</h1>
          <div className="text-[#7A7571] mt-1">{project.address}</div>
          {project.description && <div className="text-[#7A7571] mt-2 max-w-2xl">{project.description}</div>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canEdit && (
            <EditProjectButton project={project} onDone={reload} />
          )}
          {!isReadOnly && (
            <InviteMemberButton projectId={project.project_id} onDone={reload} currentMembers={members}
              picUserId={project.pic_user_id} currentUserId={user?.user_id} currentUserRole={user?.role}
              isPMorAdmin={["admin","pm"].includes(user?.role)} />
          )}
        </div>
      </div>

      {/* Payment rollup — appears only when at least one stage exists */}
      <PaymentRollupCard
        stages={paymentStages}
        contractValue={project.original_contract_value}
        onOpenTab={() => setTab("payments")}
      />

      {/* Tabs */}
      <div className="flex gap-8 border-b border-[#EBE5D9] mb-8 overflow-x-auto hide-scrollbar">
        {TABS.filter((t) => !t.firmOnly || ["admin","pm","designer"].includes(user?.role)).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`tab-btn ${currentTab === t.key ? "tab-btn-active" : ""}`}
          >
            <span className="inline-flex items-center gap-1.5">
              {t.label}
              {t.key === "feed" && feedUnread > 0 && (
                <span
                  className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-[#DC2626] text-white text-[10px] font-bold"
                  data-testid="feed-unread-badge"
                >
                  {feedUnread > 99 ? "99+" : feedUnread}
                </span>
              )}
              {t.key === "defects" && countActionable(defects, "id") > 0 && (
                <span
                  className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-[#DC2626] text-white text-[10px] font-bold"
                  data-testid="defects-badge"
                >
                  {countActionable(defects, "id")}
                </span>
              )}
            </span>
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {currentTab === "overview" && (
            <OverviewTab project={project} members={members} milestones={milestones}
              vos={vos} tasks={tasks} paymentStages={paymentStages} activity={activity} progressPct={progressPct}
              isPMorAdmin={["admin","pm"].includes(user?.role)} onChange={reload} />
          )}
          {currentTab === "vos" && (
            <VOTab projectId={project.project_id} project={project} vos={vos} paymentStages={paymentStages} onChange={reload} readOnly={isReadOnly} />
          )}
          {currentTab === "defects" && (
            <DefectsPanel
              projectId={project.project_id}
              viewer="id"
              firmContractors={firmContractors}
              firmStaff={firmStaff}
              readOnly={isReadOnly}
              onExternalChange={reload}
            />
          )}
          {currentTab === "tasks" && (
            <TaskTab projectId={project.project_id} tasks={tasks} members={members}
              milestones={milestones}
              firmContractors={firmContractors}
              firmMilestoneLibrary={firmMilestoneLibrary}
              gatedMilestoneIds={gatedMilestoneIds}
              pendingByMilestone={pendingByMilestone}
              canImport={canEdit}
              readOnly={isReadOnly}
              onChange={reload} />
          )}
          {currentTab === "payments" && (
            <PaymentsTab
              projectId={project.project_id}
              contractValue={project.original_contract_value}
              milestones={milestones}
              canManage={canEdit}
              onChange={reload}
            />
          )}
          {currentTab === "contractor-cost" && ["admin","pm","designer"].includes(user?.role) && (
            <ContractorCostTab
              projectId={project.project_id}
              project={project}
              milestones={milestones}
              tasks={tasks}
              firmContractors={firmContractors}
              readOnly={isReadOnly}
              onChange={reload}
            />
          )}
          {currentTab === "feed" && (
            <FeedTab projectId={project.project_id} unreadHook={feedUnreadHook} />
          )}
          {currentTab === "documents" && (
            <DocumentsTab projectId={project.project_id}
              canManage={canEdit}
              isCustomer={user?.role === "customer"} />
          )}
          {currentTab === "activity" && (
            <ActivityTab activity={activity} />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

/* ------------------ Overview ------------------ */
function ProjectStateCard({ project, onChange }) {
  const [open, setOpen] = useState(null); // 'on_hold' | 'dispute' | 'completed' | 'active'
  const [reason, setReason] = useState("");
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const state = project.state || "active";
  const phase = project.phase || "active";

  const togglePhase = async () => {
    const next = phase === "prep" ? "active" : "prep";
    try {
      await api.post(`/projects/${project.project_id}/phase`, { phase: next });
      toast.success(next === "prep" ? "Moved to Prep Phase" : "Moved to Active");
      onChange();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submit = async () => {
    if ((open === "on_hold" || open === "dispute") && !reason.trim()) {
      toast.error("Please give a reason so your team knows what's going on.");
      return;
    }
    if (open === "completed" && !paymentConfirmed) {
      toast.error("Confirm that payment has been received first.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/projects/${project.project_id}/state`, {
        state: open, reason: reason || null, payment_received: open === "completed" ? true : undefined,
      });
      toast.success(`Project state updated`);
      setOpen(null); setReason(""); setPaymentConfirmed(false);
      onChange();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="card-soft">
      <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-4">Project state</div>
      <div className="mb-4">
        <div className="text-lg font-display font-bold text-[#2C2A29] capitalize">{state.replace("_", " ")}</div>
        <div className="text-xs text-[#7A7571] mt-1">
          Change the state when something material happens.
        </div>
      </div>

      {/* Phase toggle — Active vs Prep Phase (pipeline). Independent of state. */}
      {state !== "completed" && (
        <div className="mb-4 pb-4 border-b border-[#EBE5D9]">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571]">Phase</div>
              <div className="text-sm font-semibold text-[#2C2A29] mt-0.5">
                {phase === "prep" ? "Prep Phase (upcoming)" : "Active"}
              </div>
            </div>
            <button
              type="button"
              onClick={togglePhase}
              data-testid="phase-toggle-btn"
              className="btn-secondary text-xs !py-1.5 !px-3 flex-shrink-0"
              title={phase === "prep"
                ? "Move this project back into active tracking"
                : "Move this project into your upcoming pipeline (Prep Phase)"}
            >
              {phase === "prep" ? "Move to Active" : "Move to Prep Phase"}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-2">
        {state !== "active" && (
          <button data-testid="state-active" onClick={() => setOpen("active")} className="btn-secondary text-sm !py-2 inline-flex items-center justify-center gap-1">
            <CheckCircle2 size={14} /> Resume as active
          </button>
        )}
        {state === "active" && (
          <>
            <button data-testid="state-on-hold" onClick={() => setOpen("on_hold")} className="btn-secondary text-sm !py-2 inline-flex items-center justify-center gap-1">
              <PauseCircle size={14} /> Put on hold
            </button>
            <button data-testid="state-dispute" onClick={() => setOpen("dispute")} className="btn-danger text-sm !py-2 inline-flex items-center justify-center gap-1">
              <ShieldAlert size={14} /> Flag dispute
            </button>
            <button data-testid="state-completed" onClick={() => setOpen("completed")} className="btn-primary text-sm !py-2 inline-flex items-center justify-center gap-1">
              <Trophy size={14} /> Mark completed
            </button>
          </>
        )}
      </div>

      <Dialog open={!!open} onOpenChange={(v) => { if (!v) { setOpen(null); setReason(""); setPaymentConfirmed(false); } }}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {open === "on_hold" && "Put project on hold"}
              {open === "dispute" && "Flag a dispute"}
              {open === "completed" && "Mark project completed"}
              {open === "active" && "Resume project"}
            </DialogTitle>
          </DialogHeader>
          {open === "on_hold" && (
            <>
              <p className="text-sm text-[#7A7571]">Every admin and PM in your firm will be alerted immediately.</p>
              <textarea data-testid="state-reason" className="input-soft min-h-[100px]" required
                placeholder="Why is this project on hold? (e.g., customer on holiday, awaiting HDB permit)"
                value={reason} onChange={(e) => setReason(e.target.value)} />
            </>
          )}
          {open === "dispute" && (
            <>
              <p className="text-sm text-[#7A7571]">This is critical priority. Managers and admins will get an immediate alert.</p>
              <textarea data-testid="state-reason" className="input-soft min-h-[100px]" required
                placeholder="Describe the dispute (what does the customer/contractor claim?)"
                value={reason} onChange={(e) => setReason(e.target.value)} />
            </>
          )}
          {open === "completed" && (
            <div className="space-y-3">
              <p className="text-sm text-[#7A7571]">A project can only be completed after handover is accepted <b>and</b> payment is received.</p>
              <label className="flex items-start gap-3 p-3 rounded-2xl bg-[#F7F4EC] cursor-pointer">
                <input data-testid="state-payment-confirm" type="checkbox" className="mt-1"
                  checked={paymentConfirmed} onChange={(e) => setPaymentConfirmed(e.target.checked)} />
                <span className="text-sm text-[#2C2A29]">
                  <b>I confirm payment has been received in full</b> and the customer has accepted handover.
                </span>
              </label>
              <textarea data-testid="state-reason" className="input-soft min-h-[80px]"
                placeholder="Optional closing note (e.g., final walkthrough date)"
                value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
          )}
          {open === "active" && (
            <p className="text-sm text-[#7A7571]">This will clear any on-hold or dispute flags and resume normal tracking.</p>
          )}
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button onClick={() => setOpen(null)} className="btn-secondary h-12">Cancel</button>
            <button onClick={submit} disabled={busy} data-testid="state-submit"
              className={`h-12 ${open === "dispute" ? "btn-danger" : "btn-primary"} inline-flex items-center justify-center`}>
              {busy ? "Saving…" : "Confirm"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OverviewTab({ project, members, milestones, vos, tasks, paymentStages = [], activity, progressPct, isPMorAdmin, onChange }) {
  const { user } = useAuth();
  const pendingVOs = vos.filter((v) => v.status === "pending");
  const reworkTasks = tasks.filter((t) => t.customer_decision === "rework_requested");
  const reviewTasks = tasks.filter((t) => t.contractor_completed_at && !t.approved_at && t.status !== "done");
  const rejectedTasks = tasks.filter((t) => t.status === "rejected" || t.customer_decision === "rejected");
  const openTasksCount = tasks.filter((t) => t.status !== "done").length;
  const attentionCount = pendingVOs.length + reworkTasks.length + reviewTasks.length + rejectedTasks.length;

  // Contractor cost is the internal spend rollup; margin is current_value − cost.
  const projectCost = tasks.reduce((s, t) => s + Number(t.contractor_cost || 0), 0);
  const currentValue = Number(project.current_project_value || 0);
  const margin = currentValue - projectCost;
  const marginPct = currentValue > 0 ? (margin / currentValue) * 100 : 0;

  // Pending VO $ — VOs still awaiting homeowner decision. Approved VOs move
  // to the "Approved VOs" bucket immediately on sign-off; payment collection
  // is tracked separately in the Payments tab.
  const pendingVOTotal = vos.reduce((sum, v) => {
    if (["draft", "draft_from_contractor", "rejected", "approved"].includes(v.status)) return sum;
    return sum + Number(v.cost_delta || 0);
  }, 0);

  const state = project.state || "active";
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        {(state === "on_hold" || state === "dispute") && (
          <div className="card-soft !bg-[#FEE2E2] !border-[#FECACA]">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-2xl bg-white flex items-center justify-center text-[#9F1239] flex-shrink-0">
                <AlertOctagon size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display text-lg font-bold text-[#9F1239]">
                  {state === "on_hold" ? "Project on hold" : "Dispute open"}
                </div>
                <div className="text-sm text-[#7A2637] mt-1">
                  {(state === "on_hold" ? project.on_hold_reason : project.dispute_reason) || "No reason recorded."}
                </div>
              </div>
            </div>
          </div>
        )}
        {state === "completed" && (
          <div className="card-soft !bg-[#DCFCE7] !border-[#BBF7D0]">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={20} className="text-[#166534]" />
              <div>
                <div className="font-display text-lg font-bold text-[#166534]">Project completed</div>
                <div className="text-xs text-[#166534]/80">
                  {project.completed_at ? new Date(project.completed_at).toLocaleDateString() : ""} · Payment received
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="card-soft">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Progress</div>
            <div className="text-sm text-[#7A7571]">{progressPct}%</div>
          </div>
          <div className="h-3 rounded-full bg-[#F7F4EC] overflow-hidden">
            <div className="h-full bg-[#81B29A] rounded-full transition-all" style={{ width: `${progressPct}%` }} />
          </div>
          {/* Project status pills — replaces the old milestone strip */}
          <div className="grid grid-cols-3 gap-3 mt-6">
            <StatusStat
              label="Pending VOs"
              value={pendingVOs.length}
              tint={pendingVOs.length > 0 ? "bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]" : "bg-[#F7F4EC] text-[#7A7571] border-[#EBE5D9]"}
              testid="ov-pending-vos"
            />
            <StatusStat
              label="Open tasks"
              value={openTasksCount}
              tint={openTasksCount > 0 ? "bg-[#EEF2FF] text-[#3730A3] border-[#C7D2FE]" : "bg-[#F7F4EC] text-[#7A7571] border-[#EBE5D9]"}
              testid="ov-open-tasks"
            />
            <StatusStat
              label="Items to review"
              value={reviewTasks.length}
              tint={reviewTasks.length > 0 ? "bg-[#DBEAFE] text-[#1E40AF] border-[#93C5FD]" : "bg-[#F7F4EC] text-[#7A7571] border-[#EBE5D9]"}
              testid="ov-to-review"
            />
          </div>
        </div>

        <div className="card-soft">
          <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-4">Cost roll-up</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-5">
            <Money label="Original contract" value={project.original_contract_value} />
            <Money label="Approved VOs" value={project.approved_vo_total} tone="positive" />
            <Money label="Pending VOs" value={pendingVOTotal} tone="warning" testid="ov-pending-vo-total" />
            <Money label="Current total" value={project.current_project_value} bold />
            <Money label="Project cost" value={projectCost} testid="ov-project-cost" />
            <MarginBlock margin={margin} pct={marginPct} hasCharge={currentValue > 0} />
          </div>
        </div>

        <div className="card-soft">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Needs your attention</div>
            {attentionCount > 0 && (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#FEE2E2] text-[#991B1B]"
                data-testid="ov-attention-count"
              >
                {attentionCount}
              </span>
            )}
          </div>
          <div className="space-y-3">
            {attentionCount === 0 && (
              <p className="text-[#7A7571]" data-testid="ov-attention-empty">Nothing critical right now. Keep it up.</p>
            )}
            {pendingVOs.slice(0, 3).map((v) => (
              <AttentionRow key={v.vo_id} label={`VO: ${v.title}`} sub={`$${Number(v.cost_delta || 0).toLocaleString()} — awaiting homeowner`} statusLabel="Pending" tint="bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]" testid={`ov-attn-vo-${v.vo_id}`} />
            ))}
            {reviewTasks.slice(0, 3).map((t) => (
              <AttentionRow key={`rv-${t.task_id}`} label={`Review: ${t.title}`} sub="Contractor marked done — verify before customer sign-off" statusLabel="To review" tint="bg-[#DBEAFE] text-[#1E40AF] border-[#93C5FD]" testid={`ov-attn-review-${t.task_id}`} />
            ))}
            {reworkTasks.slice(0, 3).map((t) => (
              <AttentionRow key={`rw-${t.task_id}`} label={`Rework: ${t.title}`} sub="Homeowner asked for a redo" statusLabel="Rework" tint="bg-[#FFEDD5] text-[#9A3412] border-[#FDBA74]" testid={`ov-attn-rework-${t.task_id}`} />
            ))}
            {rejectedTasks.slice(0, 3).map((t) => (
              <AttentionRow key={`rj-${t.task_id}`} label={`Rejected: ${t.title}`} sub="Task was rejected — needs your call" statusLabel="Rejected" tint="bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]" testid={`ov-attn-rejected-${t.task_id}`} />
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {isPMorAdmin && (
          <ProjectStateCard project={project} onChange={onChange} />
        )}
        <MembersGroup title="Team" roles={["admin", "pm", "designer"]} members={members}
          projectId={project.project_id} picUserId={project.pic_user_id}
          currentUserId={user?.user_id} currentUserRole={user?.role}
          isPMorAdmin={isPMorAdmin} onChange={onChange} />
        <MembersGroup title="Home owners" roles={["customer"]} members={members}
          projectId={project.project_id} picUserId={project.pic_user_id}
          currentUserId={user?.user_id} currentUserRole={user?.role}
          isPMorAdmin={isPMorAdmin} onChange={onChange} />
        <MembersGroup title="Contractors" roles={["contractor"]} members={members}
          projectId={project.project_id} picUserId={project.pic_user_id}
          currentUserId={user?.user_id} currentUserRole={user?.role}
          isPMorAdmin={isPMorAdmin} onChange={onChange} />

        <div className="card-soft">
          <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-4">Recent activity</div>
          <div className="space-y-3 max-h-96 overflow-auto hide-scrollbar">
            {activity.slice(0, 15).map((a) => (
              <div key={a.log_id} className="text-sm">
                <div className="text-[#2C2A29]">{a.description}</div>
                <div className="text-xs text-[#7A7571] mt-0.5">{a.actor_name} • {new Date(a.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusStat({ label, value, tint, testid }) {
  return (
    <div className={`rounded-2xl border px-3 py-3 flex flex-col items-start gap-1 ${tint}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">{label}</div>
      <div className="font-display text-2xl font-bold leading-none">{value}</div>
    </div>
  );
}

function MarginBlock({ margin, pct, hasCharge }) {
  const positive = margin >= 0;
  return (
    <div data-testid="ov-margin" className="min-w-0">
      <div className="text-xs text-[#7A7571]">Margin</div>
      <div className={`font-display text-lg font-bold leading-tight ${positive ? "text-[#166534]" : "text-[#9F1239]"}`}>
        <span className="whitespace-nowrap">
          {positive ? "+" : "−"}${Math.abs(margin).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
        {hasCharge && (
          <span className="block text-xs font-semibold mt-0.5">({pct.toFixed(1)}%)</span>
        )}
      </div>
    </div>
  );
}

function AttentionRow({ label, sub, statusLabel, tint, testid }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-2xl bg-[#F7F4EC]" data-testid={testid}>
      <div className="min-w-0">
        <div className="font-semibold text-[#2C2A29] truncate">{label}</div>
        {sub && <div className="text-xs text-[#7A7571] truncate">{sub}</div>}
      </div>
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold border flex-shrink-0 uppercase tracking-widest ${tint}`}>
        {statusLabel}
      </span>
    </div>
  );
}

function MemberActionsMenu({ member, isPic, canMakePic, canResetPassword, canRemove, onMakePic, onResetPassword, onRemove }) {
  const hasActions = canMakePic || canResetPassword || canRemove;
  if (!hasActions) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={`member-actions-${member.user_id}`}
          className="btn-ghost !p-2 text-[#7A7571] hover:text-[#2C2A29] flex-shrink-0"
          title="More actions"
          aria-label={`Actions for ${member.name || member.email || "member"}`}
        >
          <MoreHorizontal size={16} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {canMakePic && (
          <DropdownMenuItem
            data-testid={`make-pic-${member.user_id}`}
            onSelect={() => onMakePic()}
            className="cursor-pointer text-[#3730A3] font-semibold"
          >
            Make PIC
          </DropdownMenuItem>
        )}
        {canResetPassword && (
          <DropdownMenuItem
            data-testid={`reset-pw-${member.user_id}`}
            onSelect={() => onResetPassword()}
            className="cursor-pointer text-[#3730A3] font-semibold"
          >
            Reset password
          </DropdownMenuItem>
        )}
        {canRemove && (
          <>
            {(canMakePic || canResetPassword) && <DropdownMenuSeparator />}
            <DropdownMenuItem
              data-testid={`remove-member-${member.user_id}`}
              onSelect={() => onRemove()}
              className="cursor-pointer text-[#9F1239] font-semibold focus:text-[#9F1239] focus:bg-[#FEE2E2]"
            >
              {isPic ? "Remove" : `Remove ${member.role_in_project === "customer" ? "homeowner" : "member"}`}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MembersGroup({ title, roles, members, projectId, picUserId, currentUserId, currentUserRole, isPMorAdmin, onChange }) {
  const confirm = useConfirmHook();
  const list = members.filter((m) => roles.includes(m.role_in_project));
  const isStaffGroup = roles.includes("admin") || roles.includes("pm") || roles.includes("designer");
  // Only the current PIC or an admin can transfer PIC ownership.
  const canTransferPic = isStaffGroup && (currentUserRole === "admin" || picUserId === currentUserId);

  const remove = async (m) => {
    const ok = await confirm({
      title: `Remove ${m.name || "member"}?`,
      message: `They'll lose access to this project immediately.`,
      confirmText: "Remove",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/projects/${projectId}/members/${m.user_id}`);
      toast.success("Removed");
      onChange();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const makePic = async (m) => {
    const ok = await confirm({
      title: `Make ${m.name || "teammate"} the PIC?`,
      message: `PIC ownership will move from the current Person In Charge to ${m.name || "this teammate"}. All revenue/scope credit follows.`,
      confirmText: "Transfer PIC",
    });
    if (!ok) return;
    try {
      await api.patch(`/projects/${projectId}/staff-members/${m.user_id}/role`, { staff_role: "pic" });
      toast.success("PIC transferred");
      onChange();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const [resetResult, setResetResult] = useState(null); // { email, temp_password }
  const resetPassword = async (m) => {
    const ok = await confirm({
      title: `Reset ${m.name || "homeowner"}'s password?`,
      message: "A new temporary password will be generated. Share it with the homeowner — they'll be asked to change it at next login.",
      confirmText: "Reset password",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/projects/${projectId}/reset-customer-password`, { user_id: m.user_id });
      setResetResult({ email: data.email, temp_password: data.temp_password });
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const copyReset = async () => {
    if (!resetResult) return;
    try {
      await navigator.clipboard.writeText(`OrkaiFlow — homeowner sign-in\nEmail: ${resetResult.email}\nPassword: ${resetResult.temp_password}`);
      toast.success("Login details copied");
    } catch { toast.error("Couldn't copy"); }
  };

  return (
    <div className="card-soft">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">{title}</div>
        <span className="text-xs text-[#7A7571]">{list.length}</span>
      </div>
      {list.length === 0 && (
        <p className="text-sm text-[#7A7571] italic">None yet.</p>
      )}
      <div className="space-y-3">
        {list.map((m) => {
          const isPic = isStaffGroup && m.user_id === picUserId;
          return (
            <div key={m.user_id} className="flex items-center gap-3" data-testid={`member-${m.user_id}`}>
              <div className="w-9 h-9 rounded-full bg-[#F2CC8F] flex items-center justify-center text-[#2C2A29] font-bold text-sm flex-shrink-0">
                {(m.name || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-[#2C2A29] truncate flex items-center gap-1.5">
                  <span className="truncate">{m.name || m.email}</span>
                  {isPic && (
                    <span
                      data-testid={`pic-badge-${m.user_id}`}
                      className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest bg-[#E0E7FF] text-[#3730A3] border border-[#A5B4FC] flex-shrink-0"
                    >
                      PIC
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#7A7571] truncate">{m.email}</div>
              </div>
              <MemberActionsMenu
                member={m}
                isPic={isPic}
                canMakePic={!isPic && canTransferPic}
                canResetPassword={isPMorAdmin && m.role_in_project === "customer"}
                canRemove={isPMorAdmin && !isPic}
                onMakePic={() => makePic(m)}
                onResetPassword={() => resetPassword(m)}
                onRemove={() => remove(m)}
              />
            </div>
          );
        })}
      </div>
      <Dialog open={!!resetResult} onOpenChange={(v) => !v && setResetResult(null)}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="reset-pw-result">
          <DialogHeader>
            <DialogTitle>Homeowner password reset</DialogTitle>
            <DialogDescription>Share these details — they&apos;ll be asked to change the password at next login.</DialogDescription>
          </DialogHeader>
          {resetResult && (
            <>
              <div className="rounded-2xl bg-[#F7F4EC] border border-[#EBE5D9] p-3 font-mono text-sm">
                <div>Email: <span className="font-semibold">{resetResult.email}</span></div>
                <div>Password: <span className="font-semibold" data-testid="reset-pw-value">{resetResult.temp_password}</span></div>
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={copyReset} data-testid="reset-pw-copy" className="btn-secondary flex-1 text-sm">Copy details</button>
                <button type="button" onClick={() => setResetResult(null)} className="btn-primary flex-1 text-sm">Done</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Money({ label, value, tone, bold, testid }) {
  const cls = tone === "positive"
    ? "text-[#0F766E]"
    : tone === "warning"
    ? "text-[#92400E]"
    : "text-[#2C2A29]";
  return (
    <div>
      <div className="text-xs text-[#7A7571]">{label}</div>
      <div data-testid={testid} className={`${bold ? "font-display text-2xl" : "text-lg"} font-bold ${cls}`}>
        ${Number(value || 0).toLocaleString()}
      </div>
    </div>
  );
}

/* ------------------ VOs ------------------ */
function VOTab({ projectId, project, vos, paymentStages = [], onChange, readOnly = false }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVO, setEditingVO] = useState(null);
  const confirm = useConfirmHook();

  // Pending VO $ — VOs still awaiting homeowner decision. Approved VOs move
  // to the "Approved VOs" bucket immediately on sign-off; payment collection
  // is tracked separately in the Payments tab.
  const pendingVOTotal = vos.reduce((sum, v) => {
    if (["draft", "draft_from_contractor", "rejected", "approved"].includes(v.status)) return sum;
    return sum + Number(v.cost_delta || 0);
  }, 0);

  const openCreate = () => { setEditingVO(null); setDialogOpen(true); };
  const openEdit = (vo) => { setEditingVO(vo); setDialogOpen(true); };

  const send = async (vo_id) => {
    try { await api.post(`/vos/${vo_id}/send-to-customer`); toast.success("Sent to homeowner"); onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const nudge = async (vo_id) => {
    try { await api.post(`/vos/${vo_id}/nudge`); toast.success("Nudge sent"); onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (vo) => {
    const ok = await confirm({
      title: `Delete VO?`,
      message: `"${vo.title}" hasn't been sent yet. Delete it permanently?`,
      confirmText: "Delete VO",
      danger: true,
    });
    if (!ok) return;
    try { await api.delete(`/vos/${vo.vo_id}`); toast.success("Deleted"); onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="card-soft flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Cost roll-up</div>
          <div className="flex gap-6 mt-2 flex-wrap">
            <Money label="Original" value={project.original_contract_value} />
            <Money label="Approved VOs" value={project.approved_vo_total} tone="positive" />
            <Money label="Pending VOs" value={pendingVOTotal} tone="warning" testid="vo-tab-pending-total" />
            <Money label="Current total" value={project.current_project_value} bold />
          </div>
        </div>
        <button
          data-testid="raise-vo-btn"
          onClick={openCreate}
          disabled={readOnly}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus size={16} /> Raise VO
        </button>
        <VODialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          projectId={projectId}
          vo={editingVO}
          onSaved={onChange}
        />
      </div>

      {vos.length === 0 && <div className="card-soft text-[#7A7571] text-center py-12">No variation orders yet.</div>}

      {vos.map((v) => (
        <VOStaffCard
          key={v.vo_id}
          vo={v}
          readOnly={readOnly}
          onEdit={() => openEdit(v)}
          onSend={() => send(v.vo_id)}
          onNudge={() => nudge(v.vo_id)}
          onDelete={() => remove(v)}
          onChange={onChange}
        />
      ))}
    </div>
  );
}

function VOStaffCard({ vo, onEdit, onSend, onNudge, onDelete, onChange, readOnly = false }) {
  const [showTimeline, setShowTimeline] = useState(false);
  const isDraft = vo.status === "draft" || vo.status === "draft_from_contractor";
  const isRevisionReq = vo.status === "revision_requested";
  const isPending = vo.status === "pending";
  const isLocked = vo.locked;
  const canEdit = !isLocked && !readOnly && (isDraft || isRevisionReq);
  const cost = Number(vo.cost_delta || 0);
  const lastEntry = (vo.timeline || []).slice(-1)[0];
  const homeownerRemark = isRevisionReq && lastEntry?.action === "revision_requested" ? lastEntry.note : null;

  return (
    <div className="card-soft" data-testid={`vo-${vo.vo_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <h3 className="font-display text-xl font-semibold text-[#2C2A29]">{vo.title}</h3>
            <StatusBadge status={vo.status} />
            {vo.revision_count > 0 && (
              <span className="text-[10px] uppercase tracking-widest font-semibold text-[#7A7571]">
                Rev {vo.revision_count}
              </span>
            )}
          </div>
          <p className="text-sm text-[#7A7571] mb-2 whitespace-pre-wrap">{vo.description}</p>
          <p className="text-xs text-[#7A7571]">
            Raised by {vo.raised_by_name} ({vo.raised_by_role}) • {new Date(vo.created_at).toLocaleDateString()}
          </p>
          {homeownerRemark && (
            <div className="mt-3 p-3 rounded-2xl bg-[#FFEDD5] border border-[#FDBA74] text-sm text-[#7C2D12]">
              <div className="text-[10px] uppercase tracking-widest font-bold mb-1">Homeowner sent it back</div>
              <div className="italic whitespace-pre-wrap">&quot;{homeownerRemark}&quot;</div>
            </div>
          )}
          {vo.signature && (
            <div className="mt-3 p-3 rounded-2xl bg-[#F7F4EC] text-xs text-[#7A7571]">
              Digitally signed by <b className="text-[#2C2A29]">{vo.signature.signed_by_name}</b> at {new Date(vo.signature.signed_at).toLocaleString()}
            </div>
          )}
        </div>
        <div className="text-right flex flex-col items-end gap-2">
          <div className="text-2xl font-display font-bold text-[#2C2A29]">
            ${cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="flex flex-col gap-1.5 items-end">
            {canEdit && (
              <button data-testid={`vo-edit-${vo.vo_id}`} onClick={onEdit} className="btn-ghost inline-flex items-center gap-1 text-sm">
                <Pencil size={13} /> Edit
              </button>
            )}
            {(isDraft || isRevisionReq) && !readOnly && (
              <button data-testid={`vo-send-${vo.vo_id}`} onClick={onSend} className="btn-primary inline-flex items-center gap-1 text-sm !py-2 !px-4">
                <Send size={13} /> {isRevisionReq ? "Resend revised" : "Send to homeowner"}
              </button>
            )}
            {isPending && !readOnly && (
              <button data-testid={`vo-nudge-${vo.vo_id}`} onClick={onNudge} className="btn-ghost inline-flex items-center gap-1 text-sm">
                <Bell size={13} /> Nudge
              </button>
            )}
            {isDraft && !readOnly && (
              <button data-testid={`vo-delete-${vo.vo_id}`} onClick={onDelete} className="btn-ghost inline-flex items-center gap-1 text-sm text-[#9F1239]">
                <Trash2 size={13} /> Delete
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Line items table (readonly display).
          Wrapped in an overflow-x-auto so long rows / totals can scroll
          horizontally on narrow mobile viewports without truncating. */}
      {vo.line_items && vo.line_items.length > 0 && (
        <div className="mt-4 rounded-2xl bg-[#FBF7EF]/60 border border-[#EBE5D9] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[520px]">
              <thead className="bg-[#F7F4EC] text-[#7A7571]">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">Item</th>
                  <th className="px-3 py-2 text-left font-semibold">Description</th>
                  <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">Qty</th>
                  <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">Unit price</th>
                  <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">Total</th>
                </tr>
              </thead>
              <tbody>
                {vo.line_items.map((li) => (
                  <tr key={li.item_id} className="border-t border-[#EBE5D9]">
                    <td className="px-3 py-2 font-mono text-[#7A7571] whitespace-nowrap">{li.item_code || "—"}</td>
                    <td className="px-3 py-2 text-[#2C2A29]">{li.description}</td>
                    <td className="px-3 py-2 text-right text-[#2C2A29] whitespace-nowrap">{li.qty}{li.unit ? ` ${li.unit}` : ""}</td>
                    <td className="px-3 py-2 text-right text-[#2C2A29] whitespace-nowrap">${Number(li.unit_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="px-3 py-2 text-right font-semibold text-[#2C2A29] whitespace-nowrap">${Number(li.line_total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Attachments */}
      <VOAttachments
        voId={vo.vo_id}
        documents={vo.documents || []}
        canManage={!isLocked && !readOnly}
        onChange={onChange}
      />

      {/* Timeline toggle */}
      {(vo.timeline || []).length > 0 && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setShowTimeline((v) => !v)}
            className="text-xs text-[#7A7571] hover:text-[#2C2A29] underline underline-offset-2"
            data-testid={`vo-toggle-timeline-${vo.vo_id}`}
          >
            {showTimeline ? "Hide" : "Show"} timeline ({vo.timeline.length})
          </button>
          {showTimeline && <VOTimeline timeline={vo.timeline} />}
        </div>
      )}
    </div>
  );
}

/* ------------------ Project Hub (Tasks grouped by Milestone) ------------------ */
const TASK_COLS = [
  { key: "pending", label: "Pending" },
  { key: "waiting_on_designer", label: "Designer Phase" },
  { key: "waiting_on_contractor", label: "Send to contractor" },
  { key: "done", label: "Completed" },
  { key: "waiting_on_customer", label: "Update customer" },
];

const STATUS_META = {
  pending:               { dot: "#CBD5E1", bg: "#F8FAFC", fg: "#64748B", label: "Pending" },
  waiting_on_designer:   { dot: "#94A3B8", bg: "#F1F5F9", fg: "#475569", label: "Designer Phase" },
  waiting_on_contractor: { dot: "#E07A5F", bg: "#FFE4D6", fg: "#B85536", label: "With contractor" },
  waiting_on_customer:   { dot: "#14B8A6", bg: "#CCFBF1", fg: "#0F766E", label: "Customer reviewing" },
  done:                  { dot: "#81B29A", bg: "#DCFCE7", fg: "#166534", label: "Completed" },
};

function TaskRow({ task, msName, ownerName, contractorNames, expanded, onToggleExpand, onEdit, onMove, onDelete, onReloadTasks }) {
  const s = STATUS_META[task.status] || STATUS_META.waiting_on_designer;
  const [menuOpen, setMenuOpen] = useState(false);
  // Only show the "Contractor Completed – Review" pill when the contractor has
  // submitted but the ID firm hasn't approved yet. Once approved, we keep
  // contractor_completed_at for audit trail — but the pill must not re-appear
  // when the task is later moved into other statuses (e.g. Update customer).
  const needsReview = !!task.contractor_completed_at && task.status !== "done" && !task.approved_at;
  // A pending homeowner rework request is a separate condition — the ID firm
  // needs to review the homeowner's remarks and forward to the contractor.
  const customerReworkPending = task.customer_decision === "rework_requested";

  // Movement indicator label: when contractor is engaged, show name(s)
  let indicatorLabel = s.label;
  if (task.status === "waiting_on_contractor") {
    indicatorLabel = contractorNames.length ? `→ ${contractorNames.join(", ")}` : "→ Contractor";
  } else if (task.status === "waiting_on_customer") {
    indicatorLabel = "Customer reviewing";
  } else if (task.status === "waiting_on_designer") {
    indicatorLabel = "Designer Phase";
  } else if (task.status === "pending") {
    indicatorLabel = "Pending";
  }

  return (
    <div className="border-t border-[#EBE5D9] first:border-t-0" data-testid={`task-${task.task_id}`}>
      {/* Row */}
      <div
        role="button"
        tabIndex={0}
        onClick={onToggleExpand}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggleExpand(); } }}
        className={`flex items-center gap-3 px-4 py-3 transition-colors cursor-pointer ${expanded ? "bg-[#FBF7EF]" : "hover:bg-[#FBF7EF]"}`}
      >
        {/* Chevron */}
        <ChevronRight
          size={14}
          className={`text-[#B8B0A6] flex-shrink-0 transition-transform ${expanded ? "rotate-90" : ""}`}
        />
        {/* Status dot */}
        <div className="flex-shrink-0">
          <div
            className={`w-2.5 h-2.5 rounded-full ${task.status === "waiting_on_contractor" ? "animate-pulse" : ""}`}
            style={{ background: s.dot }}
            title={s.label}
          />
        </div>

        {/* Title + small meta */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className={`text-sm font-semibold text-[#2C2A29] truncate ${task.status === "done" ? "line-through opacity-60" : ""}`}>
              {task.title}
            </div>
            {task.item_code && (
              <span className="text-[10px] font-mono text-[#7A7571]">#{task.item_code}</span>
            )}
          </div>
          {task.description && task.description !== task.title && !expanded && (
            <div className="text-xs text-[#7A7571] mt-0.5 line-clamp-1">{task.description}</div>
          )}
        </div>

        {/* qty + $ */}
        <div className="hidden sm:flex flex-col items-end text-right flex-shrink-0 min-w-[100px]">
          {task.qty != null && task.unit && (
            <div className="text-[11px] text-[#7A7571]">{task.qty} {task.unit}</div>
          )}
          {task.line_total != null && (
            <div className="text-xs font-mono text-[#0F766E]">
              ${Number(task.line_total).toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </div>
          )}
        </div>

        {/* Review-required pill (contractor has flagged this for ID review) */}
        {needsReview && (
          <div
            className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap bg-[#DBEAFE] text-[#1E40AF] border border-[#93C5FD] animate-pulse"
            title="Contractor marked this complete — please review"
            data-testid={`task-review-pill-${task.task_id}`}
          >
            <ClipboardCheck size={11} />
            <span>Contractor Completed – Review</span>
          </div>
        )}

        {/* Homeowner has requested rework — ID staff must forward to contractor */}
        {customerReworkPending && (
          <div
            className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap bg-[#FEE2E2] text-[#991B1B] border border-[#FCA5A5] animate-pulse"
            title="Homeowner asked for rework — review their remarks"
            data-testid={`task-customer-rework-pill-${task.task_id}`}
          >
            <RotateCcw size={11} />
            <span>Homeowner Rework – Review</span>
          </div>
        )}

        {/* Movement indicator pill */}
        <div
          className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap"
          style={{ background: s.bg, color: s.fg }}
          data-testid={`task-status-${task.task_id}`}
        >
          {task.status === "waiting_on_contractor" && (
            <ArrowRight size={11} className="animate-pulse" />
          )}
          {task.status === "waiting_on_customer" && <Eye size={11} />}
          {task.status === "done" && <CheckCircle2 size={11} />}
          <span className="max-w-[220px] truncate">{indicatorLabel}</span>
        </div>

        {/* 3-dot menu */}
        <div className="relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
            data-testid={`task-menu-${task.task_id}`}
            className="btn-ghost !p-1.5 text-[#7A7571] hover:text-[#2C2A29]"
            aria-label="Task actions"
          >
            <MoreHorizontal size={16} />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-9 z-50 w-52 bg-white border border-[#EBE5D9] rounded-2xl shadow-lg py-2">
                <button
                  onClick={(e) => { e.stopPropagation(); onEdit(task); setMenuOpen(false); }}
                  data-testid={`task-edit-${task.task_id}`}
                  className="w-full text-left px-4 py-2 text-sm text-[#2C2A29] hover:bg-[#F7F4EC] inline-flex items-center gap-2"
                >
                  <Pencil size={13} /> Edit task
                </button>
                <div className="my-1 h-px bg-[#EBE5D9]" />
                {TASK_COLS.map((c) => (
                  <button
                    key={c.key}
                    onClick={(e) => { e.stopPropagation(); onMove(task, c.key); setMenuOpen(false); }}
                    disabled={c.key === task.status}
                    data-testid={`task-move-${task.task_id}-${c.key}`}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-[#F7F4EC] ${c.key === task.status ? "text-[#7A7571] cursor-default" : "text-[#2C2A29]"}`}
                  >
                    {c.key === task.status ? "✓ " : ""}{c.label}
                  </button>
                ))}
                <div className="my-1 h-px bg-[#EBE5D9]" />
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(task); setMenuOpen(false); }}
                  data-testid={`task-delete-${task.task_id}`}
                  className="w-full text-left px-4 py-2 text-sm text-[#9F1239] hover:bg-[#FEF2F2]"
                >
                  Delete task
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Expanded drawer */}
      {expanded && (
        <div
          className="px-12 pb-4 pt-1 bg-[#FBF7EF]"
          data-testid={`task-drawer-${task.task_id}`}
        >
          <div className="rounded-2xl bg-white border border-[#EBE5D9] p-4 space-y-3">
            {task.description && task.description !== task.title ? (
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-1">Description</div>
                <div className="text-sm text-[#2C2A29] whitespace-pre-wrap leading-relaxed">{task.description}</div>
              </div>
            ) : (
              <div className="text-sm text-[#7A7571] italic">No additional description.</div>
            )}
            {task.vo_line_items && task.vo_line_items.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-1">VO items</div>
                <ul className="rounded-xl bg-[#FBF7EF]/70 border border-[#EBE5D9] p-2 space-y-1" data-testid={`task-vo-lines-${task.task_id}`}>
                  {task.vo_line_items.map((li, idx) => (
                    <li key={idx} className="flex items-baseline gap-2 text-sm">
                      <span className="font-mono text-[11px] font-semibold text-[#7A7571] w-8 flex-shrink-0">{li.item_code || `#${idx + 1}`}</span>
                      <span className="flex-1 text-[#2C2A29] break-words">{li.description}</span>
                      {li.qty != null && (
                        <span className="text-[11px] text-[#7A7571] whitespace-nowrap">
                          Qty <b className="text-[#2C2A29]">{li.qty}{li.unit ? ` ${li.unit}` : ""}</b>
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t border-[#EBE5D9]">
              <DetailField label="Milestone" value={msName || "—"} />
              <DetailField label="Status" value={s.label} />
              <DetailField label="Assigned to" value={ownerName || "Unassigned"} />
              <DetailField label="Item code" value={task.item_code || "—"} />
              <DetailField label="Qty" value={task.qty != null ? `${task.qty} ${task.unit || ""}`.trim() : "—"} />
              <DetailField label="Unit price" value={task.unit_price != null ? `$${Number(task.unit_price).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—"} />
              <DetailField label="Line total" value={task.line_total != null ? `$${Number(task.line_total).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—"} />
              <DetailField label="Due" value={task.due_date ? new Date(task.due_date).toLocaleDateString() : "—"} />
            </div>
            <div className="pt-2 border-t border-[#EBE5D9] text-[11px] text-[#7A7571]">
              Created {task.created_at ? new Date(task.created_at).toLocaleString() : ""}
              {task.source === "import" ? " · imported from quotation" : ""}
              {task.rework_count > 0 && (
                <span className="ml-2 text-[#9A3412]">
                  · Contractor rework {task.rework_count}× (last by {task.last_rework_requested_by_name || "ID firm"})
                </span>
              )}
              {task.customer_rework_count > 0 && (
                <span className="ml-2 text-[#991B1B]">
                  · Homeowner rework {task.customer_rework_count}× (last by {task.customer_last_rework_by_name || "Homeowner"})
                </span>
              )}
            </div>

            {/* Review actions — visible only when contractor has flagged for review */}
            {needsReview && (
              <ReviewActionsBanner
                task={task}
                onReloadTasks={onReloadTasks}
              />
            )}

            {/* Homeowner has requested rework — surface remarks + one-click forward */}
            {customerReworkPending && (
              <CustomerReworkBanner
                task={task}
                onReloadTasks={onReloadTasks}
              />
            )}

            {/* Contractor updates timeline (lazy — auto-loaded on expand) */}
            <TaskContractorUpdates taskId={task.task_id} autoLoadWhen={expanded} />
          </div>
        </div>
      )}
    </div>
  );
}

function DetailField({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-0.5">{label}</div>
      <div className="text-sm text-[#2C2A29] truncate" title={String(value ?? "")}>{value}</div>
    </div>
  );
}

function CustomerReworkBanner({ task, onReloadTasks }) {
  const [latest, setLatest] = useState(null);
  const [reworkOpen, setReworkOpen] = useState(false);
  const [reworkNote, setReworkNote] = useState("");
  const [reworkFiles, setReworkFiles] = useState([]);
  const [sendingRework, setSendingRework] = useState(false);

  // Fetch the most recent customer_rework entry so we can surface the note+photos.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/tasks/${task.task_id}/contractor-updates`);
        const list = r.data.updates || [];
        const found = list.find((u) => u.status_hint === "customer_rework");
        setLatest(found || null);
      } catch (err) {
        // Silent — banner still functional without the details.
      }
    })();
  }, [task.task_id, task.customer_last_rework_at]);

  const when = task.customer_last_rework_at
    ? new Date(task.customer_last_rework_at).toLocaleString(undefined, {
        month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
      })
    : "";

  const pickReworkFiles = (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length + reworkFiles.length > 5) {
      toast.error("Max 5 reference photos");
      return;
    }
    setReworkFiles((prev) => [...prev, ...list]);
  };

  const forwardToContractor = async () => {
    if (!reworkNote.trim()) {
      toast.error("Add a short note to the contractor before forwarding");
      return;
    }
    setSendingRework(true);
    try {
      const fd = new FormData();
      fd.append("note", reworkNote.trim());
      reworkFiles.forEach((f) => fd.append("files", f));
      await api.post(`/tasks/${task.task_id}/request-rework`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Forwarded to contractor for rework");
      setReworkOpen(false);
      setReworkNote("");
      setReworkFiles([]);
      onReloadTasks && (await onReloadTasks());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingRework(false);
    }
  };

  return (
    <div
      className="mt-2 rounded-2xl bg-[#FEF2F2] border border-[#FCA5A5] p-3"
      data-testid={`customer-rework-banner-${task.task_id}`}
    >
      <div className="flex items-start gap-2 mb-2">
        <RotateCcw size={16} className="text-[#991B1B] flex-shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[#991B1B] text-sm">
            Homeowner requested rework
          </div>
          <div className="text-xs text-[#991B1B]">
            {task.customer_last_rework_by_name ? `${task.customer_last_rework_by_name} · ` : ""}
            {when}
          </div>
        </div>
      </div>
      {latest?.note && (
        <div className="rounded-xl bg-white border border-[#FECACA] p-2 text-sm text-[#7F1D1D] mb-2 whitespace-pre-wrap">
          &ldquo;{latest.note}&rdquo;
        </div>
      )}
      {latest?.images?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {latest.images.map((img) => {
            const src = `${api.defaults.baseURL}/tasks/contractor-updates/${latest.update_id}/images/${img.image_id}`;
            return (
              <a
                key={img.image_id}
                href={src}
                target="_blank"
                rel="noreferrer"
                className="w-20 h-20 rounded-xl overflow-hidden border border-[#FECACA] hover:opacity-90"
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
              </a>
            );
          })}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => {
            setReworkNote(latest?.note ? `Homeowner note: ${latest.note}\n\nContractor, please address the above.` : "");
            setReworkOpen(true);
          }}
          data-testid={`customer-rework-forward-${task.task_id}`}
          className="btn-primary text-sm inline-flex items-center gap-1.5"
        >
          <RotateCcw size={13} /> Confirm & forward to contractor
        </button>
      </div>

      <Dialog open={reworkOpen} onOpenChange={setReworkOpen}>
        <DialogContent className="max-w-3xl" data-testid={`customer-rework-forward-dialog-${task.task_id}`}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Forward to contractor</DialogTitle>
            <DialogDescription>
              The contractor will receive this note and any attached reference photos. This starts a fresh rework cycle on <span className="font-semibold text-[#2C2A29]">{task.title}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571]">
                Note to contractor
              </label>
              <textarea
                className="input-soft mt-1 text-sm min-h-[120px]"
                value={reworkNote}
                onChange={(e) => setReworkNote(e.target.value)}
                data-testid={`customer-rework-forward-note-${task.task_id}`}
              />
            </div>
            {reworkFiles.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {reworkFiles.map((f, i) => (
                  <div key={i} className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#F7F4EC] text-xs text-[#2C2A29]">
                    <ImageIcon size={11} />
                    <span className="truncate max-w-[160px]">{f.name}</span>
                    <button
                      type="button"
                      onClick={() => setReworkFiles(reworkFiles.filter((_, j) => j !== i))}
                      className="text-[#9F1239] hover:opacity-80"
                    >
                      <X size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between">
              <label className="btn-ghost text-xs inline-flex items-center gap-1 cursor-pointer">
                <Paperclip size={12} /> Attach extra photo{reworkFiles.length > 0 ? ` (${reworkFiles.length})` : ""}
                <input type="file" accept="image/*" multiple className="hidden" onChange={pickReworkFiles} />
              </label>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setReworkOpen(false)} className="btn-ghost text-sm">Cancel</button>
                <button
                  type="button"
                  onClick={forwardToContractor}
                  disabled={sendingRework || !reworkNote.trim()}
                  data-testid={`customer-rework-forward-submit-${task.task_id}`}
                  className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
                >
                  {sendingRework ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
                  {sendingRework ? "Forwarding…" : "Forward to contractor"}
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReviewActionsBanner({ task, onReloadTasks }) {
  const [approving, setApproving] = useState(false);
  const [reworkOpen, setReworkOpen] = useState(false);
  const [reworkNote, setReworkNote] = useState("");
  const [reworkFiles, setReworkFiles] = useState([]);
  const [sendingRework, setSendingRework] = useState(false);

  const when = task.contractor_completed_at
    ? new Date(task.contractor_completed_at).toLocaleString(undefined, {
        month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
      })
    : "";

  const approve = async () => {
    setApproving(true);
    try {
      await api.post(`/tasks/${task.task_id}/approve-completion`);
      toast.success("Task approved");
      onReloadTasks && (await onReloadTasks());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setApproving(false);
    }
  };

  const pickReworkFiles = (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length + reworkFiles.length > 5) {
      toast.error("Max 5 reference photos");
      return;
    }
    setReworkFiles((prev) => [...prev, ...list]);
  };

  const submitRework = async () => {
    if (!reworkNote.trim()) {
      toast.error("Please describe what needs to be redone");
      return;
    }
    setSendingRework(true);
    try {
      const fd = new FormData();
      fd.append("note", reworkNote.trim());
      reworkFiles.forEach((f) => fd.append("files", f));
      await api.post(`/tasks/${task.task_id}/request-rework`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Sent back to contractor for rework");
      setReworkOpen(false);
      setReworkNote("");
      setReworkFiles([]);
      onReloadTasks && (await onReloadTasks());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingRework(false);
    }
  };

  return (
    <div
      className="mt-2 rounded-2xl bg-[#EFF6FF] border border-[#93C5FD] p-3"
      data-testid={`review-banner-${task.task_id}`}
    >
      <div className="flex items-start gap-2 mb-2">
        <ClipboardCheck size={16} className="text-[#1E40AF] flex-shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[#1E40AF] text-sm">Contractor marked this complete</div>
          <div className="text-xs text-[#1E3A8A]">
            {task.contractor_completed_by_name ? `${task.contractor_completed_by_name} · ` : ""}
            {when}. Review the photos below, then approve or send back for rework.
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={approve}
          disabled={approving}
          data-testid={`review-approve-${task.task_id}`}
          className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
        >
          {approving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
          {approving ? "Approving…" : "Approve completion"}
        </button>
        <button
          type="button"
          onClick={() => setReworkOpen(true)}
          data-testid={`review-rework-${task.task_id}`}
          className="btn-ghost text-sm inline-flex items-center gap-1.5 border border-[#FDBA74] bg-[#FFEDD5] text-[#9A3412] hover:bg-[#FED7AA]"
        >
          <RotateCcw size={13} /> Send back for rework
        </button>
      </div>

      <Dialog open={reworkOpen} onOpenChange={setReworkOpen}>
        <DialogContent className="max-w-3xl" data-testid={`rework-dialog-${task.task_id}`}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Send back for rework</DialogTitle>
            <DialogDescription>
              Tell the contractor exactly what needs to change on <span className="font-semibold text-[#2C2A29]">{task.title}</span>. They&apos;ll get a notification and the task returns to their queue.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571]">
                What needs to be redone?
              </label>
              <textarea
                className="input-soft mt-1 text-sm min-h-[100px]"
                placeholder="e.g. The tile alignment on the left wall is off by ~5mm. Please redo the top row and share a wider shot."
                value={reworkNote}
                onChange={(e) => setReworkNote(e.target.value)}
                data-testid={`rework-note-${task.task_id}`}
              />
            </div>
            {reworkFiles.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {reworkFiles.map((f, i) => (
                  <div key={i} className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#F7F4EC] text-xs text-[#2C2A29]">
                    <ImageIcon size={11} />
                    <span className="truncate max-w-[160px]">{f.name}</span>
                    <button
                      type="button"
                      onClick={() => setReworkFiles(reworkFiles.filter((_, j) => j !== i))}
                      className="text-[#9F1239] hover:opacity-80"
                    >
                      <X size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between">
              <label className="btn-ghost text-xs inline-flex items-center gap-1 cursor-pointer">
                <Paperclip size={12} /> Attach reference photo{reworkFiles.length > 0 ? ` (${reworkFiles.length})` : ""}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={pickReworkFiles}
                  data-testid={`rework-attach-${task.task_id}`}
                />
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setReworkOpen(false)}
                  className="btn-ghost text-sm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitRework}
                  disabled={sendingRework || !reworkNote.trim()}
                  data-testid={`rework-submit-${task.task_id}`}
                  className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
                >
                  {sendingRework ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
                  {sendingRework ? "Sending…" : "Send back for rework"}
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MilestoneMenu({ milestone, onEdit, onDelete, onAddTask, onBulkAssign }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={`milestone-menu-${milestone.milestone_id}`}
          onClick={(e) => e.stopPropagation()}
          className="btn-ghost !p-2"
          title="More"
          aria-label="Milestone actions"
        >
          <MoreHorizontal size={18} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 rounded-2xl border-[#EBE5D9]" onClick={(e) => e.stopPropagation()}>
        {onAddTask && (
          <DropdownMenuItem
            data-testid={`milestone-add-task-${milestone.milestone_id}`}
            onClick={() => onAddTask(milestone)}
            className="cursor-pointer rounded-xl"
          >
            <Plus size={14} className="mr-2" /> Add new task
          </DropdownMenuItem>
        )}
        {onBulkAssign && (
          <DropdownMenuItem
            data-testid={`milestone-bulk-assign-${milestone.milestone_id}`}
            onClick={() => onBulkAssign(milestone)}
            className="cursor-pointer rounded-xl"
          >
            <Users size={14} className="mr-2" /> Send all to contractor
          </DropdownMenuItem>
        )}
        {(onAddTask || onBulkAssign) && (onEdit || onDelete) && <DropdownMenuSeparator />}
        {onEdit && (
          <DropdownMenuItem
            data-testid={`milestone-edit-${milestone.milestone_id}`}
            onClick={() => onEdit(milestone)}
            className="cursor-pointer rounded-xl"
          >
            <Pencil size={14} className="mr-2" /> Rename milestone
          </DropdownMenuItem>
        )}
        {onEdit && onDelete && <DropdownMenuSeparator />}
        {onDelete && (
          <DropdownMenuItem
            data-testid={`milestone-delete-${milestone.milestone_id}`}
            onClick={() => onDelete(milestone)}
            className="cursor-pointer rounded-xl text-[#9F1239] focus:text-[#9F1239]"
          >
            <Trash2 size={14} className="mr-2" /> Delete milestone
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MilestoneTile({ milestone, tasks, msNameById, allContractorNames, expandedTaskId, onToggleExpandTask, onEditTask, onMove, onDelete, onReorder, defaultCollapsed, paymentPending = 0, onReloadTasks, onEditMilestone, onDeleteMilestone, onAddTaskInMilestone, onBulkAssignContractor }) {
  const [collapsed, setCollapsed] = useState(!!defaultCollapsed);
  const total = tasks.length;
  // "Done" for progress purposes = ID has accepted the work. A task that's been
  // approved and then forwarded to the customer for review (waiting_on_customer)
  // is still physically complete, so it should keep counting toward the milestone %.
  const isTaskComplete = (t) => t.status === "done" || (t.status === "waiting_on_customer" && !!t.approved_at);
  const done = tasks.filter(isTaskComplete).length;
  const inProgress = tasks.filter((t) => t.status === "waiting_on_contractor").length;
  const value = tasks.reduce((sum, t) => sum + (Number(t.line_total) || 0), 0);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  // Derived milestone status: reflects task progress instead of the static field.
  //  - "completed" when every task is done
  //  - "pending" when nothing has started yet
  //  - "in_progress" otherwise
  const anyStarted = tasks.some((t) => t.status !== "pending");
  const derivedMsStatus = total === 0
    ? (milestone.status || "pending")
    : (done === total ? "completed" : (anyStarted ? "in_progress" : "pending"));

  const members = milestone.__members || [];
  const firmContractors = milestone.__firmContractors || [];
  const contractorNamesForTask = (task) => {
    if (task.status !== "waiting_on_contractor") return [];
    if (task.owner_contractor_id) {
      const fc = firmContractors.find((x) => x.contractor_id === task.owner_contractor_id);
      if (fc) return [fc.name + (fc.firm_name ? ` (${fc.firm_name})` : "")];
    }
    if (task.owner_id) {
      const m = members.find((x) => x.user_id === task.owner_id);
      if (m) return [m.name || m.email];
    }
    return allContractorNames;
  };
  const ownerNameFor = (task) => {
    if (task.owner_contractor_id) {
      const fc = firmContractors.find((x) => x.contractor_id === task.owner_contractor_id);
      if (fc) return fc.name + (fc.firm_name ? ` — ${fc.firm_name}` : "");
    }
    if (!task.owner_id) return null;
    const m = members.find((x) => x.user_id === task.owner_id);
    return m ? (m.name || m.email) : null;
  };

  const canReorder = !!milestone.milestone_id && !!onReorder;
  const currentPos = (milestone.__position ?? 0) + 1;
  const totalCount = milestone.__totalCount ?? 1;

  const commitPosition = (raw) => {
    const target = parseInt((raw || "").replace(/\D/g, ""), 10);
    if (!Number.isFinite(target) || target === currentPos) return;
    const clamped = Math.max(1, Math.min(totalCount, target));
    onReorder(milestone, clamped - 1); // 0-based target index
  };

  return (
    <div className="card-soft !p-0 overflow-visible" data-testid={`milestone-tile-${milestone.milestone_id || "none"}`}>
      {/* Header */}
      <div className="flex items-center gap-2 pr-4">
        {canReorder && (
          <div className="pl-4 py-2 flex items-center gap-2">
            <input
              key={`pos-${milestone.milestone_id}-${currentPos}`}
              type="text"
              inputMode="numeric"
              maxLength={3}
              defaultValue={String(currentPos)}
              onClick={(e) => { e.stopPropagation(); e.currentTarget.select(); }}
              onFocus={(e) => e.currentTarget.select()}
              onBlur={(e) => commitPosition(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
                else if (e.key === "Escape") { e.currentTarget.value = String(currentPos); e.currentTarget.blur(); }
              }}
              data-testid={`ms-position-${milestone.milestone_id}`}
              className="w-11 h-9 text-center rounded-xl border border-[#EBE5D9] bg-[#FBF7EF] font-mono text-sm text-[#2C2A29] focus:outline-none focus:border-[#E07A5F] focus:bg-white"
              title={`Position ${currentPos} of ${totalCount} — type a new number to move`}
            />
          </div>
        )}
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className={`flex-1 ${canReorder ? "pl-2" : "px-6"} pr-4 py-4 flex items-center gap-4 hover:bg-[#FBF7EF] transition-colors text-left rounded-3xl`}
          data-testid={`milestone-tile-header-${milestone.milestone_id || "none"}`}
        >
          <ChevronRight
            size={18}
            className={`text-[#7A7571] flex-shrink-0 transition-transform ${collapsed ? "" : "rotate-90"}`}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="font-display text-lg font-bold text-[#2C2A29] truncate">{milestone.name}</h3>
              {milestone.milestone_id && (
                <StatusBadge status={derivedMsStatus} />
              )}
              {paymentPending > 0 && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#FEF3C7] text-[#92400E]"
                  title="This milestone is linked to an unresolved payment stage"
                  data-testid={`milestone-gate-${milestone.milestone_id}`}
                >
                  <Lock size={11} /> Awaiting ${paymentPending.toLocaleString(undefined, { maximumFractionDigits: 2 })} payment
                </span>
              )}
              {inProgress > 0 && (
                <span className="text-[11px] font-semibold text-[#B85536] inline-flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#E07A5F] animate-pulse" />
                  {inProgress} in progress
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 mt-1.5 text-xs text-[#7A7571]">
              <span>{done}/{total} done</span>
              {value > 0 && (
                <span className="font-mono text-[#0F766E]">
                  ${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </span>
              )}
              <div className="flex-1 max-w-xs">
                <div className="h-1.5 rounded-full bg-[#F7F4EC] overflow-hidden">
                  <div className="h-full bg-[#81B29A] rounded-full transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
              <span className="tabular-nums">{pct}%</span>
            </div>
          </div>
        </button>

        {/* 3-dot menu: add task / bulk assign contractor / rename / delete milestone */}
        {(onEditMilestone || onDeleteMilestone || onAddTaskInMilestone || onBulkAssignContractor) && milestone.milestone_id && (
          <MilestoneMenu
            milestone={milestone}
            onEdit={onEditMilestone}
            onDelete={onDeleteMilestone}
            onAddTask={onAddTaskInMilestone}
            onBulkAssign={onBulkAssignContractor}
          />
        )}
      </div>

      {/* Body */}
      {!collapsed && (
        <div className="border-t border-[#EBE5D9]">
          {total === 0 && (
            <div className="px-6 py-6 text-sm text-[#7A7571] italic">No tasks yet in this milestone.</div>
          )}
          {tasks.map((t) => (
            <TaskRow
              key={t.task_id}
              task={t}
              msName={msNameById.get(t.milestone_id) || milestone.name}
              ownerName={ownerNameFor(t)}
              contractorNames={contractorNamesForTask(t)}
              expanded={expandedTaskId.msId === milestone.milestone_id && expandedTaskId.ids.has(t.task_id)}
              onToggleExpand={() => onToggleExpandTask(t.task_id, milestone.milestone_id)}
              onEdit={onEditTask}
              onMove={onMove}
              onDelete={onDelete}
              onReloadTasks={onReloadTasks}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* Helpers shared by New Task + Edit Task dialogs */
// Dropdown strictly reflects the Firm's Master Milestone Library.
// Optional `preserveName` keeps the currently-selected milestone visible in the
// Edit dialog if the task points at a legacy milestone no longer in the library.
function libraryMilestoneOptions(firmLibrary, preserveName = "") {
  const seen = new Set();
  const opts = [];
  (firmLibrary || []).forEach((name) => {
    const k = (name || "").toLowerCase();
    if (!k || seen.has(k)) return;
    seen.add(k);
    opts.push({ name });
  });
  if (preserveName && !seen.has(preserveName.toLowerCase())) {
    opts.push({ name: preserveName, notInLibrary: true });
  }
  return opts;
}

async function resolveMilestoneId(projectId, selectedName, projectMilestones) {
  if (!selectedName) return null;
  const found = projectMilestones.find((m) => m.name.toLowerCase() === selectedName.toLowerCase());
  if (found) return found.milestone_id;
  // Auto-create project milestone from the firm library selection
  const { data } = await api.post(`/projects/${projectId}/milestones`, { name: selectedName });
  return data.milestone?.milestone_id || data.milestone_id;
}

function AssignedToSelect({ status, milestoneName, members, firmContractors, ownerId, ownerContractorId, onChange, testIdPrefix }) {
  // Given the current status + selected milestone, filter members/contractors.
  if (status === "waiting_on_contractor") {
    const msKey = (milestoneName || "").toLowerCase();
    const recommended = [];
    const others = [];
    (firmContractors || []).forEach((c) => {
      const specs = (c.specialties || []).map((s) => s.toLowerCase());
      if (msKey && specs.includes(msKey)) recommended.push(c);
      else others.push(c);
    });
    // Sort each group: favorites first, then alphabetical
    const sortFn = (a, b) => (a.is_favorite ? -1 : 0) - (b.is_favorite ? -1 : 0) || a.name.localeCompare(b.name);
    recommended.sort(sortFn); others.sort(sortFn);
    return (
      <select className="input-soft" data-testid={`${testIdPrefix}-owner-contractor`}
        value={ownerContractorId || ""}
        onChange={(e) => onChange({ owner_id: null, owner_contractor_id: e.target.value || null })}>
        <option value="">— Unassigned —</option>
        {recommended.length > 0 && (
          <optgroup label={`Recommended${milestoneName ? ` — matches "${milestoneName}"` : ""}`}>
            {recommended.map((c) => (
              <option key={c.contractor_id} value={c.contractor_id}>
                {c.is_favorite ? "★ " : ""}{c.name}{c.firm_name ? ` — ${c.firm_name}` : ""}
              </option>
            ))}
          </optgroup>
        )}
        {others.length > 0 && (
          <optgroup label={recommended.length ? "Other contractors" : "Contractors"}>
            {others.map((c) => (
              <option key={c.contractor_id} value={c.contractor_id}>
                {c.is_favorite ? "★ " : ""}{c.name}{c.firm_name ? ` — ${c.firm_name}` : ""}
              </option>
            ))}
          </optgroup>
        )}
      </select>
    );
  }
  // Non-contractor statuses draw from project members.
  const roleFilter = status === "waiting_on_customer"
    ? (m) => m.role_in_project === "customer"
    : (m) => ["admin", "pm", "designer"].includes(m.role_in_project);
  const filtered = (members || []).filter(roleFilter);
  return (
    <select className="input-soft" data-testid={`${testIdPrefix}-owner`}
      value={ownerId || ""}
      onChange={(e) => onChange({ owner_id: e.target.value || null, owner_contractor_id: null })}>
      <option value="">— Unassigned —</option>
      {filtered.map((m) => (
        <option key={m.user_id} value={m.user_id}>{m.name} ({m.role_in_project})</option>
      ))}
    </select>
  );
}

function TaskTab({ projectId, tasks, members, milestones = [], firmContractors = [], firmMilestoneLibrary = [], gatedMilestoneIds = new Set(), pendingByMilestone = new Map(), canImport, onChange, readOnly = false }) {
  const [sp] = useSearchParams();
  const initialMs = sp.get("ms") || "all";
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [msFilter, setMsFilter] = useState(initialMs);
  const [expandedTaskId, setExpandedTaskId] = useState({ msId: null, ids: new Set() });
  const [editingTask, setEditingTask] = useState(null);
  const confirm = useConfirmHook();
  const [f, setF] = useState({
    milestone_name: "", title: "", description: "",
    owner_id: "", owner_contractor_id: "",
    status: "pending",
    item_code: "", qty: "", unit: "", unit_price: "", due_date: "",
  });

  const milestoneOptions = useMemo(
    () => libraryMilestoneOptions(firmMilestoneLibrary),
    [firmMilestoneLibrary]
  );

  const existingTasksForImport = useMemo(() => {
    const idToName = new Map(milestones.map((m) => [m.milestone_id, m.name]));
    return tasks.map((t) => ({
      title: t.title,
      item_code: t.item_code,
      milestone_name: idToName.get(t.milestone_id) || "",
    }));
  }, [tasks, milestones]);

  const create = async (e) => {
    e.preventDefault();
    try {
      const milestone_id = await resolveMilestoneId(projectId, f.milestone_name, milestones);
      const payload = {
        title: f.title,
        description: f.description,
        owner_id: f.owner_id || null,
        owner_contractor_id: f.owner_contractor_id || null,
        status: f.status,
        milestone_id,
        item_code: f.item_code || null,
        qty: f.qty === "" ? null : Number(f.qty),
        unit: f.unit || null,
        unit_price: f.unit_price === "" ? null : Number(f.unit_price),
        due_date: f.due_date || null,
      };
      await api.post(`/projects/${projectId}/tasks`, payload);
      toast.success("Task created");
      setOpen(false);
      setF({
        milestone_name: "", title: "", description: "",
        owner_id: "", owner_contractor_id: "",
        status: "pending",
        item_code: "", qty: "", unit: "", unit_price: "", due_date: "",
      });
      onChange();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const [sendContractorTask, setSendContractorTask] = useState(null);
  const [reviewImagesTask, setReviewImagesTask] = useState(null);
  // Rename-milestone dialog state.
  const [renamingMs, setRenamingMs] = useState(null); // { milestone_id, oldName, newName, saving }

  // Open the New Task dialog and pre-select the given milestone (from its 3-dot menu).
  const openAddTaskForMilestone = (ms) => {
    setF((prev) => ({ ...prev, milestone_name: ms?.name || "" }));
    setOpen(true);
  };

  const move = async (task, status) => {
    // Intercept "Send to contractor" → open assignment dialog first
    if (status === "waiting_on_contractor") {
      setSendContractorTask(task);
      return;
    }
    // Intercept "Update customer" → open image visibility review first
    if (status === "waiting_on_customer") {
      setReviewImagesTask(task);
      return;
    }
    try { await api.patch(`/tasks/${task.task_id}`, { status }); toast.success("Task updated"); onChange(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (task) => {
    const ok = await confirm({
      title: `Delete task "${task.title}"?`,
      message: "This can't be undone.",
      confirmText: "Delete",
      danger: true,
    });
    if (!ok) return;
    try { await api.delete(`/tasks/${task.task_id}`); toast.success("Task deleted"); onChange(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleExpand = (taskId, milestoneId) => {
    setExpandedTaskId((cur) => {
      // Different milestone → reset expansion, open just this task under the new milestone
      if (cur.msId !== milestoneId) {
        return { msId: milestoneId, ids: new Set([taskId]) };
      }
      // Same milestone → toggle within the set (allow multiple expanded rows)
      const nextIds = new Set(cur.ids);
      if (nextIds.has(taskId)) nextIds.delete(taskId);
      else nextIds.add(taskId);
      return { msId: milestoneId, ids: nextIds };
    });
  };

  // Milestone reorder: move `ms` to a specific 0-based target index in the project's milestone
  // list, then persist the new ordering to the backend.
  const reorderMilestone = async (ms, targetIndex) => {
    const ordered = [...milestones].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const curIdx = ordered.findIndex((m) => m.milestone_id === ms.milestone_id);
    if (curIdx < 0) return;
    const clamped = Math.max(0, Math.min(ordered.length - 1, targetIndex));
    if (clamped === curIdx) return;
    const [moved] = ordered.splice(curIdx, 1);
    ordered.splice(clamped, 0, moved);
    try {
      await api.put(`/projects/${projectId}/milestones/reorder`, {
        milestone_ids: ordered.map((m) => m.milestone_id),
      });
      onChange();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // Milestone rename — opens an in-app dialog (no more browser prompt).
  const editMilestone = (ms) => {
    setRenamingMs({ milestone_id: ms.milestone_id, oldName: ms.name || "", newName: ms.name || "", saving: false });
  };

  const submitRenameMilestone = async (e) => {
    e && e.preventDefault && e.preventDefault();
    if (!renamingMs) return;
    const clean = (renamingMs.newName || "").trim();
    if (!clean) { toast.error("Name is required"); return; }
    if (clean === renamingMs.oldName) { setRenamingMs(null); return; }
    setRenamingMs((s) => ({ ...s, saving: true }));
    try {
      await api.patch(`/milestones/${renamingMs.milestone_id}`, { name: clean });
      toast.success("Milestone renamed");
      setRenamingMs(null);
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
      setRenamingMs((s) => s && ({ ...s, saving: false }));
    }
  };

  // Milestone delete with confirmation.
  const deleteMilestone = async (ms) => {
    const ok = await confirm({
      title: `Delete milestone "${ms.name}"?`,
      message: "This will permanently delete the milestone AND every task inside it. This cannot be undone.",
      confirmText: "Delete milestone",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/milestones/${ms.milestone_id}`);
      toast.success("Milestone deleted");
      onChange();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // Bulk-assign every open task in a milestone to a single contractor.
  const [bulkAssign, setBulkAssign] = useState(null); // { milestone, contractor_id }
  const openBulkAssign = (ms) => setBulkAssign({ milestone: ms, contractor_id: "" });
  const submitBulkAssign = async () => {
    if (!bulkAssign?.contractor_id) return;
    try {
      const { data } = await api.post(
        `/milestones/${bulkAssign.milestone.milestone_id}/assign-contractor`,
        { contractor_id: bulkAssign.contractor_id },
      );
      toast.success(
        data.updated === 0
          ? "No open tasks to assign (all done already)."
          : `Assigned ${data.updated} task${data.updated === 1 ? "" : "s"} to ${data.contractor_name}`,
      );
      setBulkAssign(null);
      onChange();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const msNameById = useMemo(() => {
    const m = new Map();
    milestones.forEach((x) => m.set(x.milestone_id, x.name));
    return m;
  }, [milestones]);

  const allContractorNames = useMemo(
    () => members.filter((m) => m.role_in_project === "contractor").map((m) => m.name || m.email),
    [members]
  );

  // Build the group list (respecting the filter)
  const groups = useMemo(() => {
    const bucket = new Map();
    for (const m of milestones) bucket.set(m.milestone_id, { milestone: { ...m, __members: members, __firmContractors: firmContractors }, tasks: [] });
    // Uncategorised
    bucket.set("__none__", {
      milestone: { milestone_id: null, name: "Uncategorised", status: null, __members: members, __firmContractors: firmContractors },
      tasks: [],
    });
    for (const t of tasks) {
      const key = t.milestone_id || "__none__";
      const g = bucket.get(key);
      if (g) g.tasks.push(t);
    }
    let arr = [...bucket.values()];
    // Sort tasks inside each group: by status priority then title
    const order = { pending: 0, waiting_on_contractor: 1, waiting_on_designer: 2, waiting_on_customer: 3, done: 4 };
    for (const g of arr) g.tasks.sort((a, b) => (order[a.status] ?? 9) - (order[b.status] ?? 9));

    if (msFilter === "all") {
      // Hide empty Uncategorised tile
      arr = arr.filter((g) => g.milestone.milestone_id !== null || g.tasks.length > 0);
    } else if (msFilter === "none") {
      arr = arr.filter((g) => g.milestone.milestone_id === null);
    } else {
      arr = arr.filter((g) => g.milestone.milestone_id === msFilter);
    }
    return arr;
  }, [tasks, milestones, members, firmContractors, msFilter]);

  const totalVisible = groups.reduce((n, g) => n + g.tasks.length, 0);

  return (
    <div>
      {/* Header controls */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Filter</span>
          <select
            data-testid="task-milestone-filter"
            value={msFilter}
            onChange={(e) => setMsFilter(e.target.value)}
            className="input-soft !py-2 !px-3 text-sm w-56"
          >
            <option value="all">All milestones</option>
            <option value="none">— No milestone</option>
            {milestones.map((m) => (
              <option key={m.milestone_id} value={m.milestone_id}>{m.name}</option>
            ))}
          </select>
          <span className="text-xs text-[#7A7571]">{totalVisible} task{totalVisible === 1 ? "" : "s"}</span>
        </div>
        <div className="flex items-center gap-2">
          {canImport && !readOnly && (
            <button
              type="button"
              onClick={() => setImportOpen(true)}
              data-testid="task-import-btn"
              className="btn-secondary inline-flex items-center gap-2"
            >
              <Upload size={16} /> Import from quotation
            </button>
          )}
          {!readOnly && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button data-testid="new-task-btn" className="btn-primary inline-flex items-center gap-2"><Plus size={16} /> New task</button>
              </DialogTrigger>
            <DialogContent className="max-w-[800px] w-[95vw] rounded-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-2xl">New task</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-1 block">Milestone <span className="text-[#E07A5F]">*</span></label>
                  <select className="input-soft" data-testid="task-milestone" required value={f.milestone_name}
                    onChange={(e) => setF({ ...f, milestone_name: e.target.value })}>
                    <option value="" disabled>Choose a milestone…</option>
                    {milestoneOptions.map((m) => (
                      <option key={m.name} value={m.name}>{m.name}</option>
                    ))}
                  </select>
                </div>
                <input className="input-soft" data-testid="task-title" placeholder="Title" required
                  value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
                <textarea className="input-soft min-h-[100px]" data-testid="task-description" placeholder="Description"
                  value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-1 block">Status</label>
                    <select className="input-soft" data-testid="task-status" value={f.status}
                      onChange={(e) => setF({ ...f, status: e.target.value, owner_id: "", owner_contractor_id: "" })}>
                      {TASK_COLS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-1 block">Assign to</label>
                    <AssignedToSelect
                      status={f.status}
                      milestoneName={f.milestone_name}
                      members={members}
                      firmContractors={firmContractors}
                      ownerId={f.owner_id}
                      ownerContractorId={f.owner_contractor_id}
                      onChange={(v) => setF({ ...f, ...v })}
                      testIdPrefix="task"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-1 block">Item #</label>
                    <input className="input-soft" data-testid="task-item"
                      value={f.item_code} onChange={(e) => setF({ ...f, item_code: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-1 block">Qty</label>
                    <input type="number" step="any" className="input-soft" data-testid="task-qty"
                      value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-1 block">Unit</label>
                    <input className="input-soft" data-testid="task-unit" placeholder="Lot / nos"
                      value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-1 block">Unit $</label>
                    <input type="number" step="any" className="input-soft" data-testid="task-unit-price"
                      value={f.unit_price} onChange={(e) => setF({ ...f, unit_price: e.target.value })} />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-1 block">Due date</label>
                  <input type="date" className="input-soft" data-testid="task-due"
                    value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} />
                </div>
                <button data-testid="task-submit" className="btn-primary w-full">Create task</button>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      <TaskImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        projectId={projectId}
        onDone={onChange}
        firmMilestoneLibrary={firmMilestoneLibrary}
        existingTasks={existingTasksForImport}
      />

      {/* Milestone tiles */}
      <div className="space-y-4">
        {groups.length === 0 && (
          <div className="card-soft text-center py-12 text-[#7A7571]">
            No tasks yet. Import a quotation or add one manually.
          </div>
        )}
        {groups.map((g) => {
          // Only project milestones (skip the "Uncategorised" bucket) participate in reorder.
          const realMs = groups.filter((x) => x.milestone.milestone_id);
          const realIdx = realMs.findIndex((x) => x.milestone.milestone_id === g.milestone.milestone_id);
          const isReal = realIdx >= 0;
          const enrichedMilestone = isReal
            ? { ...g.milestone, __position: realIdx, __totalCount: realMs.length }
            : g.milestone;
          return (
            <MilestoneTile
              key={g.milestone.milestone_id || "none"}
              milestone={enrichedMilestone}
              tasks={g.tasks}
              msNameById={msNameById}
              allContractorNames={allContractorNames}
              expandedTaskId={expandedTaskId}
              onToggleExpandTask={toggleExpand}
              onEditTask={setEditingTask}
              onMove={move}
              onDelete={del}
              onReorder={isReal && msFilter === "all" ? reorderMilestone : null}
              defaultCollapsed={false}
              paymentPending={pendingByMilestone.get(g.milestone.milestone_id) || 0}
              onReloadTasks={onChange}
              onEditMilestone={g.milestone.milestone_id && !readOnly ? editMilestone : null}
              onDeleteMilestone={g.milestone.milestone_id && !readOnly ? deleteMilestone : null}
              onAddTaskInMilestone={g.milestone.milestone_id && !readOnly ? openAddTaskForMilestone : null}
              onBulkAssignContractor={g.milestone.milestone_id && !readOnly ? openBulkAssign : null}
            />
          );
        })}
      </div>

      <TaskEditDialog
        task={editingTask}
        milestones={milestones}
        firmMilestoneLibrary={firmMilestoneLibrary}
        firmContractors={firmContractors}
        projectId={projectId}
        members={members}
        onClose={() => setEditingTask(null)}
        onSaved={() => { setEditingTask(null); onChange(); }}
      />

      <SendToContractorDialog
        open={!!sendContractorTask}
        onOpenChange={(v) => { if (!v) setSendContractorTask(null); }}
        task={sendContractorTask}
        milestoneName={
          sendContractorTask
            ? (msNameById.get(sendContractorTask.milestone_id) || "")
            : ""
        }
        firmContractors={firmContractors}
        onDone={onChange}
      />

      {/* Bulk-assign every open task in a milestone to one contractor. */}
      <Dialog open={!!bulkAssign} onOpenChange={(v) => { if (!v) setBulkAssign(null); }}>
        <DialogContent className="max-w-md" data-testid="bulk-assign-dialog">
          <DialogHeader>
            <DialogTitle>Send all tasks to one contractor</DialogTitle>
            <DialogDescription>
              Every open task in <span className="font-semibold">{bulkAssign?.milestone?.name}</span> will be assigned to the contractor you pick. Tasks already marked done are skipped. You can reassign any single task later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Contractor</label>
            {firmContractors.length === 0 ? (
              <div className="text-sm text-[#7A7571] italic rounded-2xl border border-[#EBE5D9] bg-[#F7F4EC] p-4">
                No contractors added yet. Add one from the Contractors page first.
              </div>
            ) : (
              <select
                data-testid="bulk-assign-contractor-select"
                className="input-soft"
                value={bulkAssign?.contractor_id || ""}
                onChange={(e) => setBulkAssign((b) => ({ ...b, contractor_id: e.target.value }))}
              >
                <option value="">Pick a contractor…</option>
                {firmContractors.map((c) => (
                  <option key={c.contractor_id} value={c.contractor_id}>
                    {c.name}{c.trade ? ` · ${c.trade}` : ""}
                  </option>
                ))}
              </select>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setBulkAssign(null)} className="btn-ghost">Cancel</button>
              <button
                type="button"
                onClick={submitBulkAssign}
                disabled={!bulkAssign?.contractor_id}
                data-testid="bulk-assign-submit"
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Assign
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ReviewImagesBeforeCustomerDialog
        open={!!reviewImagesTask}
        onOpenChange={(v) => { if (!v) setReviewImagesTask(null); }}
        task={reviewImagesTask}
        onDone={onChange}
      />

      {/* Rename milestone dialog (replaces the previous window.prompt) */}
      <Dialog open={!!renamingMs} onOpenChange={(v) => { if (!v && !renamingMs?.saving) setRenamingMs(null); }}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="milestone-rename-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#2C2A29]">Rename milestone</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitRenameMilestone} className="space-y-4 mt-2">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-1 block">Milestone name</label>
              <input
                autoFocus
                data-testid="milestone-rename-input"
                value={renamingMs?.newName || ""}
                onChange={(e) => setRenamingMs((s) => s && ({ ...s, newName: e.target.value }))}
                className="input-soft"
                placeholder="e.g. Carpentry"
              />
            </div>
            <div className="grid grid-cols-2 gap-2 pt-2">
              <button type="button" onClick={() => setRenamingMs(null)} disabled={!!renamingMs?.saving} className="btn-secondary h-12">
                Cancel
              </button>
              <button type="submit" disabled={!!renamingMs?.saving} data-testid="milestone-rename-save" className="btn-primary h-12">
                {renamingMs?.saving ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TaskEditDialog({ task, milestones, firmMilestoneLibrary = [], firmContractors = [], projectId, members, onClose, onSaved }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  const milestoneOptions = useMemo(
    () => libraryMilestoneOptions(firmMilestoneLibrary, f?.milestone_name),
    [firmMilestoneLibrary, f?.milestone_name]
  );

  useEffect(() => {
    if (!task) { setF(null); return; }
    const currentMs = milestones.find((m) => m.milestone_id === task.milestone_id);
    setF({
      milestone_name: currentMs ? currentMs.name : "",
      title: task.title || "",
      description: task.description || "",
      owner_id: task.owner_id || "",
      owner_contractor_id: task.owner_contractor_id || "",
      status: task.status || "pending",
      item_code: task.item_code || "",
      qty: task.qty ?? "",
      unit: task.unit || "",
      unit_price: task.unit_price ?? "",
      due_date: (task.due_date || "").slice(0, 10),
    });
  }, [task, milestones]);

  const submit = async (e) => {
    e.preventDefault();
    if (!task) return;
    setBusy(true);
    try {
      const milestone_id = await resolveMilestoneId(projectId, f.milestone_name, milestones);
      const payload = {
        milestone_id,
        title: f.title,
        description: f.description || "",
        owner_id: f.owner_id || null,
        owner_contractor_id: f.owner_contractor_id || null,
        status: f.status,
        item_code: f.item_code || null,
        qty: f.qty === "" ? null : Number(f.qty),
        unit: f.unit || null,
        unit_price: f.unit_price === "" ? null : Number(f.unit_price),
        due_date: f.due_date || null,
      };
      await api.patch(`/tasks/${task.task_id}`, payload);
      toast.success("Task updated");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!task} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[800px] w-[95vw] rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="task-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Edit task</DialogTitle>
          <DialogDescription>Update task details, ownership, or costing.</DialogDescription>
        </DialogHeader>
        {f && (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-1 block">Milestone</label>
              <select className="input-soft" data-testid="edit-task-milestone" value={f.milestone_name}
                onChange={(e) => setF({ ...f, milestone_name: e.target.value })}>
                <option value="">— No milestone —</option>
                {milestoneOptions.map((m) => (
                  <option key={m.name} value={m.name}>
                    {m.name}{m.notInLibrary ? " (legacy)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <input className="input-soft" data-testid="edit-task-title" placeholder="Title" required
              value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
            <textarea className="input-soft min-h-[100px]" data-testid="edit-task-description" placeholder="Description"
              value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-1 block">Status</label>
                <select className="input-soft" data-testid="edit-task-status" value={f.status}
                  onChange={(e) => setF({ ...f, status: e.target.value, owner_id: "", owner_contractor_id: "" })}>
                  {TASK_COLS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-1 block">Assign to</label>
                <AssignedToSelect
                  status={f.status}
                  milestoneName={f.milestone_name}
                  members={members}
                  firmContractors={firmContractors}
                  ownerId={f.owner_id}
                  ownerContractorId={f.owner_contractor_id}
                  onChange={(v) => setF({ ...f, ...v })}
                  testIdPrefix="edit-task"
                />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2">
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-1 block">Item #</label>
                <input className="input-soft" data-testid="edit-task-item"
                  value={f.item_code} onChange={(e) => setF({ ...f, item_code: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-1 block">Qty</label>
                <input type="number" step="any" className="input-soft" data-testid="edit-task-qty"
                  value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-1 block">Unit</label>
                <input className="input-soft" data-testid="edit-task-unit"
                  value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-1 block">Unit $</label>
                <input type="number" step="any" className="input-soft" data-testid="edit-task-unit-price"
                  value={f.unit_price} onChange={(e) => setF({ ...f, unit_price: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-1 block">Due date</label>
              <input type="date" className="input-soft" data-testid="edit-task-due"
                value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} />
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
              <button type="submit" disabled={busy} data-testid="edit-task-submit" className="btn-primary flex-1">
                {busy ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ------------------ Feed ------------------ */
function FeedTab({ projectId, unreadHook }) {
  return <ProjectFeed projectId={projectId} canPost={true} onCounterChange={unreadHook} />;
}

/* ------------------ Activity ------------------ */
function ActivityTab({ activity }) {
  return (
    <div className="card-soft">
      <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-4">Immutable activity log</div>
      <div className="space-y-3">
        {activity.length === 0 && <p className="text-[#7A7571]">No activity yet.</p>}
        {activity.map((a) => (
          <div key={a.log_id} className="flex items-start gap-3 py-2 border-b border-[#EBE5D9] last:border-0">
            <div className="w-2 h-2 rounded-full bg-[#E07A5F] mt-2 flex-shrink-0"></div>
            <div className="flex-1">
              <div className="text-sm text-[#2C2A29]">{a.description}</div>
              <div className="text-xs text-[#7A7571] mt-0.5">{a.actor_name} ({a.actor_role}) • {new Date(a.created_at).toLocaleString()}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------ Invite member ------------------ */
function InviteMemberButton({ projectId, onDone, currentMembers = [], picUserId, currentUserId, currentUserRole, isPMorAdmin }) {
  const [open, setOpen] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [staffRole, setStaffRole] = useState("supporting");
  const [f, setF] = useState({ name: "", email: "", role_in_project: "customer", firm_contractor_id: "" });
  const [poolContractors, setPoolContractors] = useState([]);
  const [invited, setInvited] = useState(null); // {email, temp_password}

  // Only the current PIC or an admin may assign a new PIC when adding staff.
  const canAssignPic = currentUserRole === "admin" || picUserId === currentUserId;

  useEffect(() => {
    if (!open) return;
    if (isPMorAdmin) {
      (async () => {
        try {
          const { data } = await api.get("/workspace/members");
          const currentUserIds = new Set(currentMembers.map((m) => m.user_id));
          setStaffOptions((data.members || []).filter((m) => !currentUserIds.has(m.user_id)));
        } catch { /* ignore */ }
      })();
    }
    // Load contractor pool so the "Contractor" role can pick from it.
    (async () => {
      try {
        const { data } = await api.get("/workspace/contractors");
        const existing = new Set(currentMembers.map((m) => m.user_id));
        setPoolContractors((data.contractors || []).filter(
          (c) => c.user_id && !existing.has(c.user_id),
        ));
      } catch { /* ignore */ }
    })();
  }, [open, isPMorAdmin, currentMembers]);

  const addStaff = async () => {
    if (!selectedStaffId) return;
    try {
      await api.post(`/projects/${projectId}/staff-members`, {
        user_id: selectedStaffId,
        staff_role: staffRole,
      });
      toast.success(staffRole === "pic" ? "Teammate added & PIC transferred" : "Teammate added");
      setSelectedStaffId("");
      setStaffRole("supporting");
      setOpen(false);
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { role_in_project: f.role_in_project };
      if (f.role_in_project === "contractor" && f.firm_contractor_id) {
        body.firm_contractor_id = f.firm_contractor_id;
      } else {
        if (!f.name.trim() || !f.email.trim()) {
          toast.error("Name and email are required");
          return;
        }
        body.name = f.name.trim();
        body.email = f.email.trim().toLowerCase();
      }
      const { data } = await api.post(`/projects/${projectId}/members`, body);
      if (data.temp_password) {
        setInvited({ email: data.email, temp_password: data.temp_password });
      } else {
        toast.success("Added to project");
        setOpen(false);
      }
      setF({ name: "", email: "", role_in_project: "customer", firm_contractor_id: "" });
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const copyCreds = async () => {
    if (!invited) return;
    try {
      await navigator.clipboard.writeText(`OrkaiFlow — homeowner sign-in\nEmail: ${invited.email}\nPassword: ${invited.temp_password}`);
      toast.success("Login details copied");
    } catch { toast.error("Couldn't copy — long-press to copy manually"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setInvited(null); }}>
      <DialogTrigger asChild>
        <button data-testid="invite-member-btn" className="btn-secondary inline-flex items-center gap-2">
          <UserPlus size={16} /> Invite
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Invite to project</DialogTitle>
          <DialogDescription>Pick a teammate from your firm or invite someone external.</DialogDescription>
        </DialogHeader>

        {invited ? (
          <div className="card-soft !bg-[#F7F4EC] space-y-3" data-testid="invite-credentials">
            <div className="text-sm text-[#2C2A29]">
              Homeowner login created for <span className="font-semibold">{invited.email}</span>. Share these credentials — they&apos;ll be asked to change the password at first login.
            </div>
            <div className="rounded-2xl bg-white border border-[#EBE5D9] p-3 font-mono text-sm">
              <div>Email: <span className="font-semibold">{invited.email}</span></div>
              <div>Password: <span className="font-semibold" data-testid="invite-temp-password">{invited.temp_password}</span></div>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={copyCreds} data-testid="invite-copy-creds" className="btn-secondary flex-1 text-sm">Copy details</button>
              <button type="button" onClick={() => { setInvited(null); setOpen(false); }} className="btn-primary flex-1 text-sm">Done</button>
            </div>
          </div>
        ) : (
          <>
            {/* Team member picker (top) */}
            {isPMorAdmin && (
              <div className="card-soft !p-4 !bg-[#F7F4EC] space-y-3">
                <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Add a teammate</div>
                {staffOptions.length === 0 ? (
                  <p className="text-sm text-[#7A7571] italic">All your team members are already on this project.</p>
                ) : (
                  <>
                    <select
                      data-testid="invite-staff-select"
                      className="input-soft"
                      value={selectedStaffId}
                      onChange={(e) => setSelectedStaffId(e.target.value)}
                    >
                      <option value="">Pick a teammate…</option>
                      {staffOptions.map((m) => (
                        <option key={m.user_id} value={m.user_id}>
                          {m.name} · {m.role}
                        </option>
                      ))}
                    </select>
                    <div className="grid grid-cols-2 gap-2" data-testid="invite-staff-role-group">
                      <button
                        type="button"
                        onClick={() => setStaffRole("supporting")}
                        data-testid="invite-staff-role-supporting"
                        className={`rounded-2xl border px-3 py-2 text-xs font-bold uppercase tracking-widest transition ${
                          staffRole === "supporting"
                            ? "bg-[#2C2A29] text-white border-[#2C2A29]"
                            : "bg-white text-[#2C2A29] border-[#EBE5D9] hover:border-[#2C2A29]"
                        }`}
                      >
                        Supporting
                      </button>
                      <button
                        type="button"
                        onClick={() => canAssignPic && setStaffRole("pic")}
                        disabled={!canAssignPic}
                        data-testid="invite-staff-role-pic"
                        title={canAssignPic ? "Assign as Person In Charge (transfers PIC from current holder)"
                                            : "Only the current PIC or an admin can transfer PIC"}
                        className={`rounded-2xl border px-3 py-2 text-xs font-bold uppercase tracking-widest transition ${
                          staffRole === "pic"
                            ? "bg-[#3730A3] text-white border-[#3730A3]"
                            : "bg-white text-[#3730A3] border-[#EBE5D9] hover:border-[#3730A3]"
                        } ${canAssignPic ? "" : "opacity-50 cursor-not-allowed"}`}
                      >
                        PIC
                      </button>
                    </div>
                    {staffRole === "pic" && (
                      <p className="text-[11px] text-[#7A7571]" data-testid="invite-staff-pic-hint">
                        PIC will transfer from the current holder to this teammate. Only one PIC per project.
                      </p>
                    )}
                    <button
                      type="button" onClick={addStaff} disabled={!selectedStaffId}
                      data-testid="invite-staff-add"
                      className="btn-primary w-full !py-2 text-sm"
                    >
                      Add teammate
                    </button>
                  </>
                )}
              </div>
            )}

            <div className="flex items-center gap-3 my-1">
              <div className="flex-1 h-px bg-[#EBE5D9]" />
              <span className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Or invite someone new</span>
              <div className="flex-1 h-px bg-[#EBE5D9]" />
            </div>

            <form onSubmit={submit} className="space-y-3">
              {/* Role goes FIRST — determines what fields appear below. */}
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571]">Role</label>
                <select className="input-soft" data-testid="invite-role" value={f.role_in_project}
                  onChange={(e) => setF({ ...f, role_in_project: e.target.value, firm_contractor_id: "", name: "", email: "" })}>
                  <option value="customer">Home owner</option>
                  <option value="contractor">Contractor</option>
                </select>
              </div>

              {f.role_in_project === "contractor" && (
                poolContractors.length > 0 ? (
                  <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571]">Pick from your contractor pool</label>
                    <select
                      className="input-soft"
                      data-testid="invite-pool-contractor"
                      value={f.firm_contractor_id}
                      onChange={(e) => setF({ ...f, firm_contractor_id: e.target.value })}
                    >
                      <option value="">— Pick a contractor —</option>
                      {poolContractors.map((c) => (
                        <option key={c.contractor_id} value={c.contractor_id}>
                          {c.name}{c.trade ? ` · ${c.trade}` : ""}
                        </option>
                      ))}
                    </select>
                    <p className="text-[11px] text-[#7A7571] mt-2">
                      Need someone new? Add them from the <Link to="/contractors" className="font-semibold text-[#3730A3] underline">Contractors page</Link> first — that way their trade, phone, and login are captured properly.
                    </p>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-[#EBE5D9] bg-[#F7F4EC] p-3 text-sm text-[#7A7571]" data-testid="invite-empty-pool">
                    No contractors in your pool yet. Add one from the <Link to="/contractors" className="font-semibold text-[#3730A3] underline">Contractors page</Link> first, then come back here to assign them.
                  </div>
                )
              )}

              {f.role_in_project === "customer" && (
                <>
                  <input className="input-soft" data-testid="invite-name" placeholder="Name" required
                    value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
                  <input className="input-soft" data-testid="invite-email" type="email" placeholder="Email" required
                    value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
                </>
              )}
              <button
                data-testid="invite-submit"
                className="btn-primary w-full"
                disabled={f.role_in_project === "contractor" && !f.firm_contractor_id}
              >
                {f.role_in_project === "customer" ? "Create homeowner login" : "Add contractor"}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ------------------ Edit project ------------------ */
function EditProjectButton({ project, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF({
      name: project.name || "",
      address: project.address || "",
      description: project.description || "",
      start_date: (project.start_date || "").slice(0, 10),
      target_end_date: (project.target_end_date || "").slice(0, 10),
    });
  }, [open, project]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/projects/${project.project_id}`, {
        name: f.name,
        address: f.address,
        description: f.description || null,
        start_date: f.start_date || null,
        target_end_date: f.target_end_date || null,
      });
      toast.success("Project updated");
      setOpen(false);
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="edit-project-btn" className="btn-ghost inline-flex items-center gap-1 text-sm">
          <Pencil size={14} /> Edit
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Edit project</DialogTitle>
          <DialogDescription>Change project details or timeline. Contract value is derived from tasks.</DialogDescription>
        </DialogHeader>
        {f && (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">Project name</label>
              <input data-testid="ep-name" required className="input-soft" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">Address</label>
              <input data-testid="ep-address" required className="input-soft" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">Description</label>
              <textarea data-testid="ep-description" className="input-soft min-h-[80px]"
                value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
            </div>
            <div className="rounded-2xl bg-[#F7F4EC] border border-[#EBE5D9] p-3 text-xs text-[#7A7571]">
              <span className="font-semibold text-[#2C2A29]">Original contract</span>{" "}
              <span className="font-mono">${Number(project.original_contract_value || 0).toLocaleString()}</span>{" "}
              — auto-calculated from task line-items (VO tasks excluded). Current total including approved VOs: {" "}
              <span className="font-mono">${Number(project.current_project_value || 0).toLocaleString()}</span>.
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">Start</label>
                <input data-testid="ep-start" type="date" className="input-soft" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">Target end</label>
                <input data-testid="ep-end" type="date" className="input-soft" value={f.target_end_date} onChange={(e) => setF({ ...f, target_end_date: e.target.value })} min={f.start_date || undefined} />
              </div>
            </div>
            <button data-testid="ep-submit" disabled={busy} className="btn-primary w-full">
              {busy ? "Saving…" : "Save changes"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
